import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useReactToPrint } from 'react-to-print'
import { GET, POST, PUT, httpOk } from 'helpers/api'
import { formatDate, formatMoney, regexPhoneNumber, formatWorkMinutes } from 'helpers/helpers'
import BoardCompact from './BoardCompact'
import Cheque from 'components/Cheque'

function numberToUzbekWords(n) {
	if (n === 0) return 'nol'
	// Tutuq belgisiz — TTS qurilmalar uchun qulay
	const ones = ['', 'bir', 'ikki', 'uch', 'tort', 'besh', 'olti', 'yetti', 'sakkiz', 'toqqiz']
	const tens = ['', 'on', 'yigirma', 'ottiz', 'qirq', 'ellik', 'oltmish', 'yetmish', 'sakson', 'toqson']
	function h(x) {
		if (x === 0) return ''
		if (x < 10) return ones[x]
		if (x < 100) return tens[Math.floor(x/10)] + (x%10 ? ' '+ones[x%10] : '')
		if (x < 1000) return ones[Math.floor(x/100)]+' yuz'+(x%100 ? ' '+h(x%100) : '')
		const th = Math.floor(x/1000)
		return (th===1?'bir':h(th))+' ming'+(x%1000 ? ' '+h(x%1000) : '')
	}
	return h(n)
}

// Ovoz e'loni: brauzerning o'z sintezatori (Web Speech API) — bepul, kalitsiz, offline.
// Tashqi xizmat (ElevenLabs/Google TTS) va ochiq API kalitiga bog'liq emas.
const _hasTTS = typeof window !== 'undefined' && 'speechSynthesis' in window
let _voice = null

function _pickVoice() {
	if (!_hasTTS) return null
	const voices = window.speechSynthesis.getVoices()
	if (!voices || !voices.length) return null
	// Afzallik: o'zbek → rus → birinchi mavjud (o'zbek ovozi bo'lmasa rus dvigateli lotin matnni o'qiydi)
	return voices.find(v => /uz/i.test(v.lang))
		|| voices.find(v => /ru/i.test(v.lang))
		|| voices[0]
}

if (_hasTTS) {
	// Ovozlar asinxron yuklanadi — tayyor bo'lganда tanlaymiz
	_voice = _pickVoice()
	window.speechSynthesis.onvoiceschanged = () => { _voice = _pickVoice() }
}

function speakUzbek(text) {
	if (!_hasTTS) return
	try {
		if (!_voice) _voice = _pickVoice()
		const u = new SpeechSynthesisUtterance(text)
		if (_voice) { u.voice = _voice; u.lang = _voice.lang }
		else u.lang = 'ru-RU'
		u.rate = 0.95
		u.pitch = 1
		u.volume = 1
		window.speechSynthesis.speak(u) // navbat sintezator ichida boshqariladi
	} catch { /* ovoz ishlamasa asosiy oqim buzilmasin */ }
}

/* ─── TAYYOR OVOZ BO'LAKLARI (public/tts/*.mp3) ──────────────────────────────
   Windowsda o'zbek ovozi yo'q — brauzer rus dvigateliga tushib lotin matnni
   rus talaffuzida o'qirdi. E'lonlar lug'ati YOPIQ va kichik (25 so'z + harflar),
   shuning uchun har so'z bir marta yaxshi ovozda yozib olinadi va ketma-ket
   ijro etiladi: talaffuz to'g'ri, internet/API/kechikish yo'q.
   Fayllar bo'lmasa — avvalgi brauzer ovoziga qaytadi, ya'ni hech narsa buzilmaydi. */
const TTS_DIR = (process.env.PUBLIC_URL || '') + '/tts/'
// So'zlar orasidagi pauza. 0 bo'lsa so'zlar bir-biriga yopishib ketadi,
// katta bo'lsa «bo'lib-bo'lib» gapirgandek eshitiladi.
const TTS_GAP = 0.03
const TTS_KEYS = [
	'yangi-zakaz', 'qoshimcha-zakaz', 'zarur-zakaz', 'zakaz-tayyor', 'chek-raqami', 'ovoz-yoqildi',
	'bir', 'ikki', 'uch', 'tort', 'besh', 'olti', 'yetti', 'sakkiz', 'toqqiz',
	'on', 'yigirma', 'ottiz', 'qirq', 'ellik', 'oltmish', 'yetmish', 'sakson', 'toqson',
	'yuz', 'ming', 'nol', 'a', 'b', 'c', 'd',
]

let _ctx = null
const _bufs = new Map() // kalit -> AudioBuffer yoki null (fayl yo'q)

function _audioCtx() {
	if (!_ctx) {
		const C = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext)
		if (!C) return null
		_ctx = new C()
	}
	if (_ctx.state === 'suspended') _ctx.resume()
	return _ctx
}

// Bo'lakni bir marta yuklab, dekodlangan holda saqlaymiz.
// Kengaytma o'zi topiladi — Aisha .mp3, bo'lish vositasi .wav beradi.
async function _buffer(key) {
	if (_bufs.has(key)) return _bufs.get(key)
	const ctx = _audioCtx()
	if (!ctx) return null
	for (const ext of ['wav', 'mp3']) {
		try {
			const r = await fetch(TTS_DIR + key + '.' + ext)
			if (!r.ok) continue
			// ⚠️ SPA serveri yo'q faylga index.html (200) qaytaradi — u dekodlanmaydi
			// va shu yerda xato beradi, ya'ni qo'shimcha tekshiruv kerak emas.
			const buf = await ctx.decodeAudioData(await r.arrayBuffer())
			_bufs.set(key, buf)
			return buf
		} catch { /* keyingi kengaytmani sinaymiz */ }
	}
	_bufs.set(key, null)
	return null
}

// Ovoz yoqilganda hammasini oldindan yuklab qo'yamiz — birinchi e'lon kechikmasin.
function warmClips() {
	if (_audioCtx()) TTS_KEYS.forEach(k => { _buffer(k) })
}

/* E'lonni ANIQ VAQTDA rejalashtiradi.
   Ilgari har bo'lak <audio> bilan navbatma-navbat ijro etilardi va oradagi
   bo'shliqni brauzer o'zi belgilardi (sust kompyuterda 100-300 ms) — shuning
   uchun «bo'lib-bo'lib» eshitilardi. Endi hammasi oldindan dekodlanadi va
   bittasi tugashi bilan keyingisi boshlanadigan qilib rejalashtiriladi.
   Yo'q bo'lak jim o'tkaziladi: faqat iboralar bo'lsa ham e'lon ishlaydi. */
async function announce(keys, fallbackText) {
	const ctx = _audioCtx()
	if (!ctx) return speakUzbek(fallbackText)
	const bufs = (await Promise.all(keys.filter(Boolean).map(_buffer))).filter(Boolean)
	if (!bufs.length) return speakUzbek(fallbackText)
	let t = ctx.currentTime + 0.06 // rejalashtirishga ulgurish uchun kichik zaxira
	for (const b of bufs) {
		const src = ctx.createBufferSource()
		src.buffer = b
		src.connect(ctx.destination)
		src.start(t)
		t += b.duration + TTS_GAP
	}
}

// Bo'lakni fallbacksiz chalish: fayl bo'lmasa JIM o'tadi (chapak kabi
// tovushlarni brauzer sintezatori o'qiy olmaydi, urinish ham kerak emas).
function playEffect(key) {
	const ctx = _audioCtx()
	if (!ctx) return
	_buffer(key).then(b => {
		if (!b) return
		const src = ctx.createBufferSource()
		src.buffer = b
		src.connect(ctx.destination)
		src.start(ctx.currentTime + 0.02)
	})
}

// Sonni bo'lak nomlariga ajratadi: 6827 -> ['olti','ming','sakkiz','yuz','yigirma','yetti']
function numberToClips(n) {
	return numberToUzbekWords(n).split(' ').filter(Boolean)
}

/* ─── BAYRAM / KECHIKISH EFFEKTLARI ─────────────────────────────────────────
   Usta zakazni «Tayyor» qilganda: vadadan oldin bo'lsa mushak otiladi va
   chapak chalinadi, kech bo'lsa xafa stiker tepadan tushadi.
   Canvas ishlatiladi (yuzlab DOM elementi emas) — monoblok i5-4avlod, sust.
   Ovoz: `chapak` bo'lagi bo'lsa chalinadi, bo'lmasa jim (majburiy emas). */
const LATE_TOLERANCE = 15 * 60 * 1000 // 15 daqiqagacha kechikish — «kech» sanalmaydi

function fireConfetti() {
	if (typeof document === 'undefined') return
	const cv = document.createElement('canvas')
	cv.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2000'
	document.body.appendChild(cv)
	const ctx = cv.getContext('2d')
	const w = cv.width = window.innerWidth, h = cv.height = window.innerHeight
	const colors = ['#e11d48', '#2563eb', '#059669', '#d97706', '#7c3aed', '#0891b2']
	// Ikki chetdan otiladigan mushak
	const parts = []
	for (let side = 0; side < 2; side++) {
		const ox = side === 0 ? w * 0.12 : w * 0.88
		for (let i = 0; i < 70; i++) {
			const ang = (side === 0 ? -60 : -120) * Math.PI / 180 + (Math.random() - 0.5) * 1.1
			const sp = 9 + Math.random() * 13
			parts.push({
				x: ox, y: h * 0.92, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp,
				c: colors[(Math.random() * colors.length) | 0],
				sz: 4 + Math.random() * 6, rot: Math.random() * 6, vr: (Math.random() - 0.5) * 0.3,
			})
		}
	}
	let t = 0
	const tick = () => {
		t++
		ctx.clearRect(0, 0, w, h)
		parts.forEach(p => {
			p.vy += 0.28            // tortishish
			p.vx *= 0.99
			p.x += p.vx; p.y += p.vy; p.rot += p.vr
			ctx.save()
			ctx.translate(p.x, p.y); ctx.rotate(p.rot)
			ctx.globalAlpha = Math.max(0, 1 - t / 150)
			ctx.fillStyle = p.c
			ctx.fillRect(-p.sz / 2, -p.sz / 2, p.sz, p.sz * 0.6)
			ctx.restore()
		})
		if (t < 150) requestAnimationFrame(tick)
		else cv.remove()
	}
	requestAnimationFrame(tick)
}

/* Xafa stiker — 💩 shaklida, lekin ko'zlari XAFA (standart emoji tabassum
   qiladi, bu yerda esa aksincha kerak). Shuning uchun emoji emas, SVG:
   qoshlar ichkariga ko'tarilgan, qorachiqlar pastga qaragan, og'iz teskari yoy. */
const SAD_POO_SVG = `
<svg viewBox="0 0 120 120" width="100%" height="100%" aria-hidden="true">
  <defs>
    <linearGradient id="pA" x1=".25" y1="0" x2=".75" y2="1">
      <stop offset="0" stop-color="#a9793f"/><stop offset=".55" stop-color="#84552a"/><stop offset="1" stop-color="#5a381a"/>
    </linearGradient>
    <linearGradient id="pB" x1=".25" y1="0" x2=".75" y2="1">
      <stop offset="0" stop-color="#b5854a"/><stop offset=".55" stop-color="#8d5d2f"/><stop offset="1" stop-color="#63401f"/>
    </linearGradient>
    <linearGradient id="pC" x1=".25" y1="0" x2=".75" y2="1">
      <stop offset="0" stop-color="#c0904f"/><stop offset=".55" stop-color="#966633"/><stop offset="1" stop-color="#6d4723"/>
    </linearGradient>
    <radialGradient id="gloss" cx=".5" cy=".5" r=".5">
      <stop offset="0" stop-color="#fff" stop-opacity=".42"/><stop offset="1" stop-color="#fff" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="shadow" cx=".5" cy=".5" r=".5">
      <stop offset="0" stop-color="#2b1a0c" stop-opacity=".38"/><stop offset="1" stop-color="#2b1a0c" stop-opacity="0"/>
    </radialGradient>
  </defs>

  <!-- yerga tushgan soya -->
  <ellipse cx="60" cy="108" rx="42" ry="7" fill="url(#shadow)"/>

  <!-- pastki g'altak -->
  <path fill="url(#pA)" d="M8 88c0-13 21-21 52-21s52 8 52 21c0 11-21 18-52 18S8 99 8 88z"/>
  <path fill="#3f2712" opacity=".28" d="M12 92c8 9 26 14 48 14s40-5 48-14c-2 10-22 17-48 17s-46-7-48-17z"/>
  <ellipse cx="42" cy="74" rx="24" ry="7" fill="url(#gloss)"/>

  <!-- o'rta g'altak -->
  <path fill="url(#pB)" d="M20 62c0-11 17-18 40-18s40 7 40 18c0 9-17 15-40 15s-40-6-40-15z"/>
  <path fill="#3f2712" opacity=".25" d="M23 66c7 7 21 11 37 11s30-4 37-11c-2 8-17 13-37 13s-35-5-37-13z"/>
  <ellipse cx="46" cy="52" rx="17" ry="5" fill="url(#gloss)"/>

  <!-- yuqori g'altak -->
  <path fill="url(#pC)" d="M32 41c0-9 12-15 28-15s28 6 28 15c0 7-12 12-28 12s-28-5-28-12z"/>
  <path fill="#3f2712" opacity=".22" d="M35 45c5 5 15 8 25 8s20-3 25-8c-1 6-11 10-25 10s-24-4-25-10z"/>
  <ellipse cx="52" cy="34" rx="12" ry="4" fill="url(#gloss)"/>

  <!-- uchidagi burama -->
  <path fill="url(#pC)" d="M50 27c-1-8 2-14 8-17 4-2 7 0 6 4-1 5-6 8-8 13z"/>
  <path fill="#fff" opacity=".22" d="M54 14c2-2 5-3 7-2-2 0-4 2-5 4z"/>

  <!-- ── YUZ ── -->
  <!-- ko'zlar: qovoq bilan, xafa -->
  <g>
    <ellipse cx="45" cy="82" rx="9.5" ry="10.5" fill="#fdfdfd"/>
    <ellipse cx="75" cy="82" rx="9.5" ry="10.5" fill="#fdfdfd"/>
    <circle cx="45" cy="86" r="5" fill="#1c0f05"/>
    <circle cx="75" cy="86" r="5" fill="#1c0f05"/>
    <circle cx="43.2" cy="84" r="1.7" fill="#fff" opacity=".9"/>
    <circle cx="73.2" cy="84" r="1.7" fill="#fff" opacity=".9"/>
    <!-- yuqori qovoq: ichkariga qiyshaygan (xafalik) -->
    <path fill="#7a5027" d="M35.5 82c0-5.8 4.3-10.5 9.5-10.5 3.6 0 6.7 2.2 8.3 5.5-3.4-2.2-13.1-1.8-17.8 5z"/>
    <path fill="#7a5027" d="M84.5 82c0-5.8-4.3-10.5-9.5-10.5-3.6 0-6.7 2.2-8.3 5.5 3.4-2.2 13.1-1.8 17.8 5z"/>
    <!-- qoshlar -->
    <path d="M33 68q9-6 18-2" stroke="#241206" stroke-width="3.4" fill="none" stroke-linecap="round"/>
    <path d="M87 68q-9-6-18-2" stroke="#241206" stroke-width="3.4" fill="none" stroke-linecap="round"/>
  </g>

  <!-- og'iz: teskari yoy, biroz ochiq -->
  <path d="M50 100q10-9 20 0" stroke="#241206" stroke-width="3.6" fill="none" stroke-linecap="round"/>
</svg>`


function dropSadSticker() {
	if (typeof document === 'undefined') return
	// Qizil chaqnash — kechikish sezilarli bo'lsin, yolg'iz stiker kam ta'sir qilardi
	const flash = document.createElement('div')
	flash.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:1999;'
		+ 'background:radial-gradient(ellipse at center,rgba(225,29,72,0) 35%,rgba(225,29,72,.34) 100%)'
	document.body.appendChild(flash)
	flash.animate([{ opacity: 0 }, { opacity: 1, offset: .3 }, { opacity: 1, offset: .7 }, { opacity: 0 }],
		{ duration: 2800, easing: 'ease-out' }).onfinish = () => flash.remove()

	const el = document.createElement('div')
	el.innerHTML = SAD_POO_SVG
	el.style.cssText = 'position:fixed;left:50%;top:-240px;transform:translateX(-50%);z-index:2000;'
		+ 'width:min(38vw,240px);aspect-ratio:1;pointer-events:none;'
		+ 'filter:drop-shadow(0 14px 30px rgba(0,0,0,.5))'
	document.body.appendChild(el)
	// Tushadi -> yerga urilib ikki marta sakraydi -> pastga ketadi
	const anim = el.animate([
		{ top: '-240px', transform: 'translateX(-50%) scale(1) rotate(-8deg)', opacity: 0 },
		{ top: '34%', transform: 'translateX(-50%) scale(1) rotate(2deg)', opacity: 1, offset: .28 },
		{ top: '34%', transform: 'translateX(-50%) scale(1.14,.86) rotate(0deg)', offset: .34 },
		{ top: '27%', transform: 'translateX(-50%) scale(.95,1.06) rotate(-2deg)', offset: .44 },
		{ top: '34%', transform: 'translateX(-50%) scale(1.06,.94) rotate(1deg)', offset: .54 },
		{ top: '32%', transform: 'translateX(-50%) scale(1) rotate(0deg)', offset: .62 },
		{ top: '32%', transform: 'translateX(-50%) scale(1) rotate(0deg)', opacity: 1, offset: .8 },
		{ top: '115%', transform: 'translateX(-50%) scale(1) rotate(6deg)', opacity: 0 },
	], { duration: 2800, easing: 'cubic-bezier(.35,.6,.4,1)' })
	anim.onfinish = () => el.remove()
}

// Bo'lim ranglari (oq fonda o'qiladigan to'q tuslar)
const DEPARTMENTS = [
	{ type: 4, label: 'Profnastil', accent: '#2563eb' },
	{ type: 3, label: 'Aksessuar',  accent: '#7c3aed' },
	{ type: 5, label: 'Tunukafon',  accent: '#0891b2' },
]
const OMBOR_ACCENT = '#059669'

// Shoshilinchlik (deadline bo'yicha)
const URGENCY = {
	danger:    { accent: '#e11d48', label: 'KECHIKKAN' },
	warning:   { accent: '#d97706', label: 'BUGUN' },
	success:   { accent: '#059669', label: 'MULJALDA' },
	secondary: { accent: '#64748b', label: 'VADASIZ' },
}

function getUrgency(delivery_date) {
	if (!delivery_date) return 'secondary'
	const now = new Date()
	const deadline = new Date(delivery_date)
	if (deadline < now) return 'danger'
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const deadlineDay = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate())
	if (deadlineDay.getTime() === todayStart.getTime()) return 'warning'
	return 'success'
}

function urgencyOrder(delivery_date) {
	const u = getUrgency(delivery_date)
	return u === 'danger' ? 1 : u === 'warning' ? 2 : u === 'success' ? 3 : 4
}

/**
 * Keyingi yangilanishgacha qolgan soniya.
 *
 * ⚠️ NEGA ALOHIDA KOMPONENT (2026-08-05): sanoq ilgari Board ning O'ZIDA
 * `useState` bo'lib turardi va har soniyada `setCountdown` chaqirilardi.
 * Board — 2500 satrlik komponent; har tik butun kengashni qaytadan chizardi,
 * va render ichida kartalar qayta filtrlanib, qayta saralanardi. 3-5 ta karta
 * bo'lganda bu sezilmasdi, 276 ta kartada esa monoblok ekrani QOTIB qolardi.
 *
 * Endi soniya faqat SHU kichik komponentni qayta chizadi — Board tegilmaydi.
 * `React.memo` — ota qayta chizilganda ham `lastUpdated` o'zgarmasa tinch turadi.
 */
const BoardClock = React.memo(function BoardClock({ lastUpdated }) {
	const [countdown, setCountdown] = useState(30)

	useEffect(() => {
		setCountdown(30)
		const timer = setInterval(() => setCountdown(p => (p > 0 ? p - 1 : 0)), 1000)
		return () => clearInterval(timer)
	}, [lastUpdated])

	return (
		<span className="pb-clock">
			{formatDate(lastUpdated, 'HH:mm:ss')}
			<b className={countdown < 6 ? 'urgent' : ''}>{countdown}s</b>
		</span>
	)
})

/* OMBOR tartibi: eng UZOQ kutgani tepada. Sexlardagi `sortOrders` vada bo'yicha
   saralaydi, omborda esa kartochkada kutish vaqti yozilgan — tartib boshqa
   narsani ko'rsatsa, qizil «KECH CHIQYAPTI» chek ro'yxat o'rtasida qolib ketardi.
   ZARUR baribir birinchi: uni sotuvchi ataylab belgilagan. */
function sortForWarehouse(orders) {
	const waited = (o) => o.ready_at ? Date.now() - new Date(o.ready_at) : -1
	return [...orders].sort((a, b) => {
		const ua = a.is_urgent ? 0 : 1, ub = b.is_urgent ? 0 : 1
		if (ua !== ub) return ua - ub
		const d = waited(b) - waited(a)   // ko'p kutgani oldinda
		return d !== 0 ? d : a.id - b.id
	})
}

/**
 * `mode`:
 *   'vada'  — vada bo'yicha (sukut, usta uchun tabiiy)
 *   'eski'  — eng eski birinchi (rahbar tashlandiqni tozalayotganda qulay)
 * ZARUR zakaz ikkala rejimda ham tepada qoladi.
 */
function sortOrders(orders, mode = 'vada') {
	if (mode === 'eski') {
		return [...orders].sort((a, b) => {
			const ua = a.is_urgent ? 0 : 1, ub = b.is_urgent ? 0 : 1
			if (ua !== ub) return ua - ub
			return new Date(a.cheque_opened_at || 0) - new Date(b.cheque_opened_at || 0)
		})
	}
	return [...orders].sort((a, b) => {
		// ZARUR zakaz har doim tepada — vada bo'yicha saralashdan USTUN.
		// Sotuvchi uni ataylab belgilagan va kvota bilan cheklangan, ya'ni
		// «hammasi zarur» bo'lib ketmaydi.
		const ua = a.is_urgent ? 0 : 1, ub = b.is_urgent ? 0 : 1
		if (ua !== ub) return ua - ub
		const diff = urgencyOrder(a.delivery_date) - urgencyOrder(b.delivery_date)
		if (diff !== 0) return diff
		if (a.delivery_date && b.delivery_date)
			return new Date(a.delivery_date) - new Date(b.delivery_date)
		return a.id - b.id
	})
}

function relativeDay(dateStr) {
	if (!dateStr) return null
	const d = new Date(dateStr)
	const now = new Date()
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate())
	const diff = Math.round((today - dDay) / 86400000)
	const time = formatDate(d, 'HH:mm')
	if (diff === 0) return { label: 'Bugun', time, color: '#34d399' }
	if (diff === 1) return { label: 'Kecha', time, color: '#f59e0b' }
	if (diff === 2) return { label: 'Ilgari kuni', time, color: '#f43f5e' }
	return { label: formatDate(d, 'dd.MM.yyyy'), time, color: '#94a3b8' }
}

// Vada kunini SO'Z bilan. `relativeDay()` dan farqi — kelajakni ham biladi
// (vada ertaga bo'lishi mumkin, chek ochilgan sana esa faqat o'tmishda).
// Uzoq kunlar so'z bilan chalkashtirmaydi — sana ko'rsatiladi.
function dayWord(dateStr) {
	if (!dateStr) return null
	const d = new Date(dateStr)
	const now = new Date()
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate())
	const diff = Math.round((dDay - today) / 86400000) // musbat = kelajak
	if (diff === 0) return 'Bugun'
	if (diff === 1) return 'Ertaga'
	if (diff === 2) return 'Indinga'
	if (diff === -1) return 'Kecha'
	if (diff === -2) return 'Ilgari kuni'
	return formatDate(d, 'dd.MM')
}

function elapsedTime(fromStr) {
	if (!fromStr) return null
	const diffMin = Math.floor((Date.now() - new Date(fromStr)) / 60000)
	if (diffMin < 60) return `${diffMin} daqiqa`
	const h = Math.floor(diffMin / 60), m = diffMin % 60
	if (h < 24) return m > 0 ? `${h} soat ${m} daqiqa` : `${h} soat`
	const d = Math.floor(h / 24), rh = h % 24
	return rh > 0 ? `${d} kun ${rh} soat` : `${d} kun`
}

function chequeNo(order) {
	return order.parent_id
		? `${order.parent_id}-${order.cheque_suffix}`
		: order.cheque_suffix === 'A' ? `${order.id}-A` : order.id
}

/** Kechikish sabablari — backenddagi OrderControlController::LATE_REASONS bilan BIR XIL */
const LATE_REASON_LABELS = {
	seller_hold:   "Sotuvchi to'xtatdi",
	material:      'Material yetmadi',
	client_change: "Mijoz o'zgartirdi",
	machine:       'Stanok buzildi',
	workload:      "Navbat ko'p edi",
	other:         'Boshqa',
}
function lateReasonLabel(k) { return LATE_REASON_LABELS[k] || k }

/** Chek ochilganidan beri necha kun o'tgan (rahbar uchun: yosh vadadan muhimroq) */
function ageDays(openedAt) {
	if (!openedAt) return null
	const d = Math.floor((Date.now() - new Date(openedAt).getTime()) / 86400000)
	return d > 0 ? d : null
}

/**
 * `managerView` — ishlab chiqarish RAHBARI ko'rinishi (2026-08-01, user qarori).
 *
 * Rahbar ishni BAJARMAYDI, kuzatadi. Shuning uchun undan:
 *   • «Jarayonga olish» olib tashlandi — u ustaning amali. Rahbar bossa
 *     audit_log ga UNING ismi yoziladi va «qaysi usta qancha ishlaydi»
 *     statistikasi buziladi (ma'lumot ifloslanadi).
 *   • 80mm/A4 olib tashlandi — ish topshirig'ini usta chiqaradi.
 *   • Sotuvchi qatori olib tashlandi — har kartochkada bir xil yozuv edi.
 *   • «Taxminiy vaqt» olib tashlandi — jami ustun sarlavhasida bor.
 * Qo'shildi: kim ishlayapti, necha kundan beri turibdi, mijoz telefoni.
 */
function OrderCard({ order, deptType, isOmbor, onShip, index, onPrint, onAdvance, managerView, onPause, onResume, onTimeline, onLateReason }) {
	const u = getUrgency(order.delivery_date)
	const urg = URGENCY[u]
	const dept = deptType != null
		? (order.departments || []).find(d => Number(d.product_type) === deptType)
		: null
	const opened = relativeDay(order.cheque_opened_at)

	if (isOmbor) {
		const ready = order.warehouse_ready
		const anyReady = (order.departments || []).some(d => d.dept_ready)
		return (
			<div className="pb-card" style={{ '--stripe': ready ? OMBOR_ACCENT : '#f59e0b' }}>
				<div className="pb-card-top">
					<div className="pb-cheque">
						<span className="pb-cheque-hash">#</span>{chequeNo(order)}
					</div>
					<span className={'pb-badge ' + (ready ? 'is-green badge-alarm-blink-green' : 'is-amber badge-alarm-blink-yellow')}>
						{ready ? 'Tayyor' : 'Jarayonda'}
					</span>
				</div>

				<div className="pb-depts">
					{(order.departments || []).map((d, i) => (
						<span key={i} className={'pb-chip ' + (d.dept_ready ? 'is-green' : 'is-amber badge-alarm-blink-yellow')}>
							{d.type_name}
						</span>
					))}
				</div>

				<div className="pb-meta">
					<div className="pb-client"><i className="uil-user" />{order.client_name}</div>
					<div className="pb-seller"><i className="uil-user-circle" />{order.cashier_name}</div>
				</div>

				{opened && (
					<div className="pb-strip">
						<span className="pb-strip-l">Qabul qilindi</span>
						<span className="pb-strip-v" style={{ color: opened.color }}>{opened.label} <em>{opened.time}</em></span>
					</div>
				)}

				<div className={'pb-strip ' + (ready ? 'is-done' : 'is-progress')}>
					<span className="pb-strip-l">{ready ? 'Bajarildi' : 'Jarayonda'}</span>
					<span className="pb-strip-v">{elapsedTime(order.cheque_opened_at)}</span>
				</div>

				<div className="pb-actions" style={{ flexWrap: 'wrap' }}>
					{!managerView &&
					<button
						className={'pb-btn ' + (anyReady ? 'is-ship' : 'is-wait')}
						disabled={!anyReady}
						onClick={() => anyReady && onShip && onShip(order)}
						style={{ flex: '1 1 100%' }}
					>
						<i className={anyReady ? 'uil-truck' : 'uil-clock'} />
						{anyReady ? 'Chiqarib yuborish' : 'Kutilmoqda...'}
					</button>}
					{!managerView && onPrint && (<>
						<button className="pb-btn is-ghost" onClick={() => onPrint(order, false)} title="80mm chek"><i className="uil-print" /> 80mm</button>
						<button className="pb-btn is-ghost" onClick={() => onPrint(order, true)} title="A4 chek"><i className="uil-print" /> A4</button>
					</>)}
					{/* YO'LI — aynan OMBORDA eng kerak. Chekning butun yo'li
					    («yangi buyurtma» dan «tayyor» gacha) shu yerda tugaydi,
					    ya'ni «qayerda kechikdi» degan savol shu ustunda beriladi. */}
					{onTimeline && (
						<button className="pb-btn is-ghost" style={{ flex: '1 1 100%' }}
							onClick={() => onTimeline(order)}>
							<i className="uil-clock-three" /> Yo'li
						</button>
					)}
				</div>
			</div>
		)
	}

	return (
		<div className="pb-card" style={{ '--stripe': urg.accent }}>
			<div className="pb-card-top">
				<div className="pb-id-wrap">
					{index != null && <span className="pb-index" style={{ background: urg.accent }}>{index}</span>}
					<div>
						<div className="pb-cheque">
							<span className="pb-cheque-hash">#</span>{chequeNo(order)}
							{order.parent_id && <span className="pb-add">qo'shimcha</span>}
						</div>
						{opened && <div className="pb-opened">{opened.label} · {opened.time}</div>}
					</div>
				</div>
				{dept && (
					<span
						className={'pb-badge' + (dept.is_initial ? ' badge-alarm-blink' : '')}
						style={{ background: dept.is_initial ? '#f43f5e' : (dept.status_color || '#64748b') }}
					>
						{dept.status_name}
					</span>
				)}
			</div>

			<div className="pb-meta">
				<div className="pb-client"><i className="uil-user" />{order.client_name}</div>
				{managerView
					? <>
						{order.client_phone &&
							<div className="pb-seller"><i className="uil-phone" />{regexPhoneNumber(order.client_phone)}</div>}
						{order.last_actor &&
							<div className="pb-seller" title="Oxirgi harakat">
								<i className="uil-user-check" />{order.last_actor}
							</div>}
						{(() => {
							const age = ageDays(order.cheque_opened_at)
							if (age == null) return null
							return (
								<div className="pb-seller" title="Ochilganidan beri"
									style={{ color: age > 7 ? '#b42318' : undefined }}>
									<i className="uil-hourglass" />{age} kun turibdi
								</div>
							)
						})()}
					</>
					: <div className="pb-seller"><i className="uil-user-circle" />{order.cashier_name}</div>}
			</div>

			{order.delivery_date ? (
				<div className="pb-deadline" style={{ '--u': urg.accent }}>
					<div className="pb-deadline-l">{urg.label}</div>
					<div className="pb-deadline-d">{formatDate(order.delivery_date, 'dd.MM.yyyy')}</div>
					<div className="pb-deadline-t">{formatDate(order.delivery_date, 'HH:mm')}</div>
					{/* KECHIKISH SABABI.
					    KO'RSATISH — hammaga (rahbar aynan shuni nazorat qiladi).
					    YOZISH — faqat `onLateReason` berilganda, ya'ni sabab bilgan
					    odamga: usta va sotuvchiga. Rahbar sabab TO'QIMAYDI. */}
					{u === 'danger' && (
						order.late_reason
							? <div className={'pb-late-reason' + (onLateReason ? '' : ' is-ro')}
								onClick={onLateReason ? () => onLateReason(order) : undefined}>
								<i className="uil-info-circle" />{lateReasonLabel(order.late_reason)}
							</div>
							: onLateReason
								? <button className="pb-late-ask" onClick={() => onLateReason(order)}>
									<i className="uil-question-circle" /> Sababini belgilang
								</button>
								: <div className="pb-late-reason is-ro is-missing">
									<i className="uil-question-circle" />Sabab yozilmagan
								</div>
					)}
				</div>
			) : (
				<div className="pb-deadline is-none">
					<i className="uil-clock" /> Vada belgilanmagan
				</div>
			)}

			{/* TO'XTATILGAN — sotuvchining og'zaki «to'xtatib turing» si endi shu yerda
			    KO'RINADI. Usta uni bosmaydi, rahbar esa kim to'xtatganini biladi. */}
			{order.is_paused && (
				<div className="pb-paused">
					<div className="pb-paused-top">
						<i className="uil-pause-circle" /> TO'XTATILGAN
					</div>
					<div className="pb-paused-who">{order.paused_by}</div>
					{order.pause_reason && <div className="pb-paused-why">{order.pause_reason}</div>}
				</div>
			)}

			{!managerView && dept && Number(dept.estimated_minutes) > 0 && (() => {
				const min = Math.round(dept.estimated_minutes)
				const h = Math.floor(min / 60), m = min % 60
				const label = h > 0 ? `~${h} soat${m > 0 ? ` ${m} daqiqa` : ''}` : `~${m} daqiqa`
				return (
					<div className="pb-strip is-est">
						<span className="pb-strip-l"><i className="uil-clock" /> Taxminiy vaqt</span>
						<span className="pb-strip-v">{label}</span>
					</div>
				)
			})()}

			<div className="pb-actions">
				{!managerView && onAdvance && dept && !dept.dept_ready && dept.next_status_id && (
					<button className={'pb-btn ' + (dept.is_initial ? 'is-advance' : 'is-ready')} onClick={() => onAdvance(order.id, deptType, dept.next_status_id)}>
						<i className={dept.is_initial ? 'uil-play' : 'uil-check'} /> {dept.is_initial ? 'Jarayonga olish' : 'Tayyor'}
					</button>
				)}
				{!managerView && onPrint && (<>
					<button className="pb-btn is-ghost" onClick={() => onPrint(order, false)} title="80mm chek">
						<i className="uil-print" /> 80mm
					</button>
					<button className="pb-btn is-ghost" onClick={() => onPrint(order, true)} title="A4 chek">
						<i className="uil-print" /> A4
					</button>
				</>)}
				{/* To'xtatish/davom ettirish tugmalari bu KO'RINISHDA berilmaydi:
				    bu — rahbar oynasi, u nazorat qiladi, ishni to'xtatmaydi.
				    Proplar saqlangan — kelajakda boshqa rol shu kartochkani
				    ishlatsa, tugmalar o'zi paydo bo'ladi. */}
				{!order.is_paused && onPause && (
					<button className="pb-btn is-ghost" onClick={() => onPause(order)} title="To'xtatib turish">
						<i className="uil-pause" /> To'xtatish
					</button>
				)}
				{order.is_paused && onResume && (
					<button className="pb-btn is-resume" onClick={() => onResume(order)} title="Davom ettirish">
						<i className="uil-play" /> Davom ettirish
					</button>
				)}
				{onTimeline && (
					<button className="pb-btn is-ghost" onClick={() => onTimeline(order)} title="Chek yo'li">
						<i className="uil-clock-three" /> Yo'li
					</button>
				)}
			</div>
		</div>
	)
}

/* ─────────────────────────────────────────────────────────────────────────────
   SEX/OMBOR KO'RINISHI — 4 ustunli kanban (2026-07-28)
   Monoblokda bir vaqtda faqat bitta chek ko'rinardi. Endi zakazlar BOSQICH
   bo'yicha 4 ustunga bo'linadi va kartochkalar ixcham.
   ⚠️ Faqat ROLE_WORKSHOP va ROLE_WAREHOUSE uchun. Ishlab chiqarish rahbari,
   direktor, kassir — eski ko'rinishda qoladi (user talabi), shuning uchun
   `OrderCard` ga UMUMAN tegilmagan.
   ───────────────────────────────────────────────────────────────────────────── */
/* SVETOFOR (2026-09-01, egasining talabi): uchala ish bosqichi bir xil mantiqda —
   Yangi = QIZIL, Jarayonda = SARIQ, Tayyor = YASHIL. Ilgari «Tayyor» KO'K edi va
   ixcham ko'rinishdagi nuqtalar bilan mos kelmasdi. «Chiqib ketganlar» — arxiv
   ustuni, tegilmadi. */
const KB_STAGES = [
	{ key: 'new',      label: 'Yangi zakaz',     accent: '#e11d48' },
	{ key: 'progress', label: 'Jarayonda',       accent: '#d97706' },
	{ key: 'ready',    label: 'Tayyor',          accent: '#16a34a' },
	{ key: 'shipped',  label: 'Chiqib ketganlar', accent: '#059669' },
]

// Buyurtma qaysi bosqichda. `deptTypes` — foydalanuvchi ko'radigan bo'lim turlari
// (null = hammasi, ombor uchun). Bosqichlar `products_group_statuses` ga mos:
// 1 Yangi Buyurtma -> new · 2 Jarayonda -> progress · 3 Buyurtma Tayyor -> ready.
function kbStageOf(order, deptTypes, isWarehouse) {
	const depts = (order.departments || []).filter(d =>
		!deptTypes || deptTypes.includes(Number(d.product_type)))
	// Ombor uchun «tayyor» = mavjud `warehouseOrders` mantig'i bilan AYNAN bir xil.
	// ⚠️ Bu tekshiruv `depts.length === 0` dan OLDIN turishi shart: `warehouse_ready`
	// bo'lgan, lekin bo'lim qatori bo'sh zakaz aks holda ustundan tushib qolardi va
	// omborchi uni chiqarib yubora olmasdi (sinovda 2 ta o'rniga 1 ta chiqqan edi).
	if (isWarehouse && (order.warehouse_ready || depts.some(d => d.dept_ready))) return 'ready'
	if (depts.length === 0) return null
	if (!isWarehouse && depts.every(d => d.dept_ready)) return 'ready'
	if (depts.every(d => d.is_initial)) return 'new'
	return 'progress'
}

// Bo'limning ISH HAJMI. Birlik bo'limga qarab boshqacha — `StatsBar` dagi
// hisob bilan AYNAN bir xil bo'lishi shart, aks holda kartochkalar yig'indisi
// tepadagi umumiy raqamga to'g'ri kelmaydi.
// Butun son bo'lsa kasrsiz: «10 metr», «100 metr». Kasri bo'lsa keragicha
// ko'rsatiladi va oxiridagi nollar olib tashlanadi («10.5 metr», «0.25 kv.m»).
// Ilgari doim `toFixed` edi va «10.0 m» chiqib turardi.
function trimNum(v, digits) {
	return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(digits)))
}

function deptAmount(d) {
	const pt = Number(d?.product_type)
	if (pt === 4) { const v = Number(d.total_metr || 0);      return v > 0 ? trimNum(v, 1) + ' metr' : null }
	// ⚠️ tur 3 da `total_roll_metr` EMAS: u rulondan ketgan metr (ombor hisobi).
	// Sotuvchi yozgan va usta ishlaydigan miqdor — `total_metr` (chekdagi 100 metr).
	if (pt === 3) { const v = Number(d.total_metr || 0);      return v > 0 ? trimNum(v, 2) + ' metr' : null }
	if (pt === 5) { const v = Number(d.total_kv_metr || 0);   return v > 0 ? trimNum(v, 3) + ' kv.m' : null }
	const q = Number(d?.total_qty || 0)
	return q > 0 ? trimNum(q, 2) + ' dona' : null
}

// Ustun sarlavhasidagi jami ish hajmi. Birlik bo'limga bog'liq (m / kv.m / dona),
// shuning uchun turlar aralashganda (ombor) qo'shilmaydi — null qaytadi.
function kbColAmount(orders, deptTypes) {
	if (!deptTypes || deptTypes.length !== 1) return null
	const pt = deptTypes[0]
	let sum = 0
	orders.forEach(o => {
		const d = (o.departments || []).find(x => Number(x.product_type) === pt)
		if (!d) return
		sum += Number(pt === 4 || pt === 3 ? d.total_metr
			: pt === 5 ? d.total_kv_metr : d.total_qty) || 0
	})
	if (!(sum > 0)) return null
	if (pt === 4) return trimNum(sum, 1) + ' metr'
	if (pt === 3) return trimNum(sum, 2) + ' metr'
	if (pt === 5) return trimNum(sum, 3) + ' kv.m'
	return trimNum(sum, 2) + ' dona'
}

/* Ustun sarlavhasidagi ISH VAQTI (usta uchun) — «navbatda necha soatlik ish bor».
   Manba `departments[].estimated_minutes` (ChequeController::board): hajm ÷ sof
   tezlik + rulon sozlash — sana tavsiyasi bilan bir xil model.

   ⚠️ FAQAT «Янги заказ» va «Жараёнда» ustunlarida. «Тайёр» — ish allaqachon
      bajarilgan (navbat emas), «Чиқиб кетган» cheklarda esa backend `departments`
      blokini umuman qaytarmaydi — u yerda raqam doim 0 chiqardi.
   ⚠️ OMBOR (kbDeptTypes = null) ko'rmaydi: omborchiga ishlab chiqarish hajmi
      ataylab ko'rsatilmaydi (kartochkalarda ham `hideWork`).
   ⚠️ Bir necha bo'limli ustada HAMMA bo'limi qo'shiladi — daqiqa birligi bitta,
      metrdan farqli o'laroq qo'shsa bo'ladi (metrda kv.m va uzunlik aralashadi).
   ⚠️ Bo'limi TAYYOR bo'lgan chek sanalmaydi (aks holda navbat shishadi). */
function kbColMinutes(orders, deptTypes, stageKey) {
	if (!deptTypes || !deptTypes.length) return null
	if (stageKey !== 'new' && stageKey !== 'progress') return null
	let min = 0
	orders.forEach(o => {
		(o.departments || []).forEach(d => {
			if (!deptTypes.includes(Number(d.product_type))) return
			if (d.dept_ready) return
			min += Number(d.estimated_minutes || 0)
		})
	})
	return formatWorkMinutes(min)
}

/* Bir xil G'ALTAKDAGI zakazlarni guruhlaydi — usta rulonni bir marta o'rnatib
   bir nechtasini bajarishi mumkin. Kalit `balance_id` (jismonan bitta g'altak),
   rulon NOMI emas: bitta nom ikki xil g'altakda bo'ladi (OK RUH/030 TMZ ->
   1070 va 302) va ular alohida o'rnatish.
   Tartib: ro'yxat allaqachon saralangan (zarur -> vada), guruhlar esa BIRINCHI
   uchragan tartibda — shunda eng shoshilinch guruh tepada qoladi.
   Sarlavha faqat 2+ zakazli guruhga chiziladi: bittalik zakazda u bekorga joy
   egallaydi, rulon nomi kartochkaning o'zida ko'rsatiladi. */
function groupByRoll(orders, deptType) {
	const map = new Map()
	for (const o of orders) {
		const d = (o.departments || []).find(x => Number(x.product_type) === deptType)
		const key = d && d.balance_id ? String(d.balance_id) : `_${o.id}`
		if (!map.has(key)) {
			map.set(key, { key, name: d?.roll_name || null, mixed: Number(d?.roll_count || 0) > 1, orders: [], metr: 0 })
		}
		const g = map.get(key)
		g.orders.push(o)
		const v = d ? Number(deptType === 5 ? d.total_kv_metr : d.total_metr) || 0 : 0
		g.metr += v
	}
	return [...map.values()]
}

/* OMBOR uchun HUDUD bo'yicha guruhlash — bir yo'nalishdagi cheklar birga tursin.
   Yaqinlikni dastur taxmin qilmaydi: u Nastroykadagi hudud daraxtidan olinadi
   (Viloyat -> Tuman), ya'ni yaqinlikni foydalanuvchi belgilagan. Manzil matnidan
   guruhlash mumkin emas — u erkin yoziladi va deyarli to'ldirilmaydi.
   Hududi yo'q cheklar oxirgi guruhda. */
function groupByRegion(orders) {
	const map = new Map()
	for (const o of orders) {
		const key = o.region_id ? String(o.region_id) : '_yoq'
		if (!map.has(key)) {
			map.set(key, { key, name: o.region_name || null, parent: o.region_parent || null, orders: [] })
		}
		map.get(key).orders.push(o)
	}
	const arr = [...map.values()]
	// «Belgilanmagan» har doim oxirida — u yo'nalish emas
	return arr.filter(g => g.key !== '_yoq').concat(arr.filter(g => g.key === '_yoq'))
}

/* OMBOR: zakaz tayyor bo'lgandan beri qancha kutayotgani.
   Chegaralar (user belgilagan): 40 daqiqagacha tinch, 40 daq - 2 soat
   ogohlantirish, 2 soatdan keyin kech chiqyapti. */
const WAIT_WARN = 40 * 60, WAIT_LATE = 2 * 3600
function waitLevel(sec) {
	if (sec === null) return null
	if (sec >= WAIT_LATE) return { color: '#e11d48', label: 'KECH CHIQYAPTI' }
	if (sec >= WAIT_WARN) return { color: '#d97706', label: 'KUTMOQDA' }
	return { color: '#059669', label: 'TAYYOR' }
}

/* ═══════════════════════════════════════════════════════════════════════
   OMBORCHI QATORI — KbCard'ning IXCHAM shakli (2026-08-26).

   Egasining talabi: «faqat ixchamlikni qil, jarayonlariga tegma».
   Shuning uchun bu KbCard bilan AYNAN BIR XIL ma'lumot va amallar —
   faqat katta kartochka o'rniga bitta zich qator:
     [#chek ●sabab] [mijoz · tel] [bo'lim chiplari] [kutish] [amallar]
   Hudud guruhlash, kutish taymeri, chiqarish oqimi, bosilganda vaqt
   modali — hammasi joyida qoladi. Sex (KbCard) TEGILMAGAN.
   ═══════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════
   HAYDOVCHI TANLASH BLOKI — IKKI oynada ishlatiladi (2026-08-26):
     1) «Chiqarib yuborish» — MAJBURIY (yoki «Mijoz o'zi olib ketadi»);
     2) Yuk xatini qayta chop etish (haydovchi yozilmagan eski cheklar).
   Bitta komponent — ikki nusxa forma bir-biridan uzoqlashib ketmasin.

   st = { mode, own, recent, name, phone, car, selfPickup }
   set(patch) — parent modal state'iga qo'shib yozadi.
   ═══════════════════════════════════════════════════════════════════════ */
function DriverPicker({ st, set, showSelfPickup = false }) {
	const self = showSelfPickup && st.selfPickup
	return (
		<div>
			{showSelfPickup &&
				<div className="form-check form-switch mb-2">
					<input className="form-check-input" type="checkbox" id="selfPick"
						checked={!!st.selfPickup}
						onChange={e => set({ selfPickup: e.target.checked })} />
					<label className="form-check-label" htmlFor="selfPick">
						<b>Mijoz o'zi olib ketadi</b> — haydovchi kerak emas
					</label>
				</div>}

			{!self && <>
				<div className="btn-group btn-group-sm w-100 mb-2">
					<button type="button"
						className={'btn ' + (st.mode === 'own' ? 'btn-primary' : 'btn-outline-secondary')}
						onClick={() => set({ mode: 'own' })}>
						<i className="uil-star me-1" />Ўзимизники
					</button>
					<button type="button"
						className={'btn ' + (st.mode !== 'own' ? 'btn-primary' : 'btn-outline-secondary')}
						onClick={() => set({ mode: 'street' })}>
						<i className="uil-user me-1" />Четдан
					</button>
				</div>

				{st.mode === 'own' && <>
					{(st.own || []).length === 0 &&
						<div className="text-muted mb-2" style={{ fontSize: 12.5 }}>
							Рўйхат бўш — Xodimlar bo'limiga «Haydovchi» lavozimi bilan
							qo'shing yoki pastga yozib «Рўйхатга сақлаш»ни босинг.
						</div>}
					<div className="d-flex gap-1 flex-wrap mb-2">
						{(st.own || []).map(r => (
							<span key={`${r.source}-${r.id}`} className="d-inline-flex align-items-center">
								<button type="button"
									className={'btn btn-sm ' + (st.name === r.name ? 'btn-primary' : 'btn-outline-secondary')}
									style={r.source === 'manual' ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 } : {}}
									title={r.source === 'employee' ? 'Xodimlar bo\'limidan (lavozimi: Haydovchi)' : ''}
									onClick={() => set({ name: r.name || '', phone: r.phone || '', car: r.car || '' })}>
									{r.source === 'employee' && <i className="uil-user-check me-1" style={{ fontSize: 12 }} />}
									{r.name}{r.car ? ` · ${r.car}` : ''}
								</button>
								{r.source === 'manual' &&
									<button type="button" className="btn btn-sm btn-outline-danger px-1"
										style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0 }}
										title="Ro'yxatdan olib tashlash"
										onClick={async () => {
											if (!window.confirm(`${r.name} ro'yxatdan olib tashlansinmi?`)) return
											const res = await PUT(`/drivers/${r.id}/delete`, {}, { loader: false })
											if (httpOk(res)) set({ own: (st.own || []).filter(x => !(x.source === 'manual' && x.id === r.id)) })
										}}>×</button>}
							</span>
						))}
					</div>
				</>}

				{st.mode !== 'own' && (st.recent || []).length > 0 && <>
					<div className="text-muted mb-1" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.05em' }}>
						Oxirgi ishlatilganlar
					</div>
					<div className="d-flex gap-1 flex-wrap mb-2">
						{st.recent.map((r, i) => (
							<button key={i} type="button"
								className={'btn btn-sm ' + (st.name === r.driver_name ? 'btn-primary' : 'btn-outline-secondary')}
								onClick={() => set({ name: r.driver_name || '', phone: r.driver_phone || '', car: r.driver_car || '' })}>
								{r.driver_name}{r.driver_car ? ` · ${r.driver_car}` : ''}
							</button>
						))}
					</div>
				</>}

				<label className="form-label mb-1">Haydovchi ismi</label>
				<input className="form-control" value={st.name}
					onChange={e => set({ name: e.target.value })} />
				<div className="row mt-2">
					<div className="col-6">
						<label className="form-label mb-1">Telefoni</label>
						<input className="form-control" value={st.phone} placeholder="90 123-45-67"
							onChange={e => set({ phone: e.target.value })} />
					</div>
					<div className="col-6">
						<label className="form-label mb-1">Mashina raqami</label>
						<input className="form-control" value={st.car} placeholder="30 662 QBA"
							onChange={e => set({ car: e.target.value })} />
					</div>
				</div>
				{st.name.trim() &&
					!(st.own || []).some(r => r.name.toLowerCase() === st.name.trim().toLowerCase()) &&
					<div className="d-flex justify-content-end mt-2">
						<button type="button" className="btn btn-sm btn-outline-success"
							onClick={async () => {
								const res = await POST('/drivers', { name: st.name, phone: st.phone, car: st.car }, { loader: false })
								if (httpOk(res)) {
									const own = await GET('/drivers', {}, { loader: false })
									if (httpOk(own)) set({ own: own.data || [] })
								}
							}}>
							<i className="uil-star me-1" />Рўйхатга сақлаш
						</button>
					</div>}
			</>}
		</div>
	)
}

/** Qator muddati — SO'Z bilan (shortDur ning «28k 16s» i qatorda o'qilmasdi) */
function rowDur(sec) {
	const s = Math.max(0, Math.round(Number(sec) || 0))
	const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60)
	if (d > 0) return `${d} кун${h > 0 ? ` ${h} соат` : ''}`
	if (h > 0) return `${h} соат${m > 0 ? ` ${m} дақ` : ''}`
	return `${Math.max(1, m)} дақ`
}

/** Kutish yorlig'i — qisqa (KECH CHIQYAPTI qatorga sig'may «qichqirardi») */
const ROW_WAIT = { '#e11d48': 'КЕЧ', '#d97706': 'КУТМОҚДА', '#059669': 'ТАЙЁР' }

function KbRow({ order, stage, onShip, onPrint, onTiming, onStall, onPhotos }) {
	const wsec = order.ready_at ? Math.max(0, (Date.now() - new Date(order.ready_at)) / 1000) : null
	const lv = stage === 'ready' ? waitLevel(wsec) : null
	const depts = order.departments || []
	const stop = (e) => e.stopPropagation()

	/* IKKI QATORLI maket (2026-08-26): 4 ustunda bitta qator qisilib,
	   mijoz ismi butunlay yo'qolib, tugmalar kesilib qolardi. Endi:
	     1-qator: #chek · telefon · ism (uzuni uch nuqta bilan)
	     2-qator: chiplar (chapda) — muddat pilli + tugmalar (o'ngda) */
	const pill = (bg, color, text) => (
		<span style={{
			background: bg, color, fontWeight: 700, fontSize: 10.5,
			whiteSpace: 'nowrap', padding: '2px 7px', borderRadius: 9,
			fontVariantNumeric: 'tabular-nums', flex: 'none',
		}}>{text}</span>
	)

	let timeEl = null
	if (lv) {
		timeEl = pill(lv.color + '22', lv.color, `${ROW_WAIT[lv.color] || lv.label} · ${rowDur(wsec)}`)
	} else if (stage === 'new' || stage === 'progress') {
		if (order.delivery_date) {
			const dd = new Date(order.delivery_date)
			const now = new Date()
			const passed = dd.getTime() < now.getTime()
			const today = dd.toDateString() === now.toDateString()
			const c = passed ? '#e11d48' : today ? '#d97706' : '#64748b'
			timeEl = pill(c + '22', c, `${passed ? 'КЕЧ · ' : ''}${formatDate(order.delivery_date, 'dd.MM HH:mm')}`)
		} else {
			timeEl = <span style={{ opacity: .55, fontSize: 12, whiteSpace: 'nowrap' }}>вада йўқ</span>
		}
	} else if (stage === 'shipped' && (order.shipped_at || order.cheque_closed_at)) {
		timeEl = <span style={{ opacity: .65, fontSize: 12.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
			{formatDate(order.shipped_at || order.cheque_closed_at, 'HH:mm')}
		</span>
	}

	/* Tugma — btn-sm ham katta edi: o'zimizniki, 22px balandlik */
	const act = (title, icon, cls, onClick) => (
		<button type="button" className={'btn ' + cls}
			style={{ padding: '1px 7px', fontSize: 12.5, lineHeight: 1.5 }}
			title={title} onClick={onClick}>
			<i className={icon} />
		</button>
	)

	return (
		<div className="kb-row" onClick={() => onTiming && onTiming(order)}
			role="button" tabIndex={0}
			style={{
				display: 'flex', flexDirection: 'column', gap: 2,
				padding: '4px 8px', borderBottom: '1px solid var(--dk-border, #e9edf3)',
				cursor: 'pointer', lineHeight: 1.2,
			}}>
			{/* 1-QATOR: chek raqami · telefon · ism */}
			<div style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}>
				<span style={{ fontWeight: 800, fontSize: 14, whiteSpace: 'nowrap', letterSpacing: '-.2px' }}>
					#{chequeNo(order)}
					{order.stall_reason &&
						<i title={order.stall_note || 'Sabab yozilgan'}
							style={{ color: '#f59e0b', fontStyle: 'normal', fontSize: 12 }}> ●</i>}
					{order.is_urgent && <i className="uil-bolt" style={{ color: '#e11d48', fontSize: 12 }} />}
				</span>
				{order.client_phone &&
					<span style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
						{order.client_phone}
					</span>}
				<span style={{
					flex: '1 1 auto', minWidth: 0, fontSize: 12, opacity: .9,
					overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
				}}>{order.client_name || '—'}</span>
			</div>

			{/* 2-QATOR: chiplar — muddat + tugmalar.
			   ⚠️ chiplar konteynerida overflow:hidden SHART — busiz chip matni
			      qisqarmay, muddat pilli USTIGA chiqib yopishib qolardi. */}
			<div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
				{stage !== 'shipped' && (depts.length > 0
					? <span style={{
						display: 'inline-flex', gap: 3, flex: '1 1 auto', minWidth: 0,
						overflow: 'hidden', whiteSpace: 'nowrap',
					}}>
						{depts.map((d, i) => (
							<span key={i} title={d.type_name + (d.dept_ready ? ' — tayyor' : ' — jarayonda')}
								style={{
									fontSize: 10, padding: '1px 5px', borderRadius: 7, whiteSpace: 'nowrap',
									background: d.dept_ready ? 'var(--ok-mid-bg, #e8f5e9)' : 'var(--an-chip-bg, #f1f3f7)',
									color: d.dept_ready ? 'var(--ok-mid, #059669)' : 'var(--dk-text-2, #64748b)',
								}}>
								{d.dept_ready && '✓'}{d.type_name}
							</span>
						))}
					</span>
					: <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: 10, opacity: .7, whiteSpace: 'nowrap', overflow: 'hidden' }}>
						{order.all_type_names ? <><i className="uil-box" /> ombordan</> : null}
					</span>)}
				{stage === 'shipped' && <span style={{ flex: '1 1 auto' }} />}

				{timeEl}

				<span style={{ display: 'inline-flex', gap: 3, flex: 'none' }} onClick={stop}>
					{stage === 'ready' && onShip &&
						act('Chiqarib yuborish', 'uil-truck', 'btn-success', () => onShip(order))}
					{onPrint &&
						act('Yuk xati', 'uil-print', 'btn-outline-secondary', () => onPrint(order))}
					{/* YUK RASMI (2026-09-01): chiqib ketgan yukda rasm bor-yo'qligi
					    darhol ko'rinsin — bor bo'lsa yashil kamera, yo'q bo'lsa xira.
					    Bosilganda rasm ochiladi (omborchi/rahbar tekshiradi). */}
					{stage === 'shipped' && onPhotos &&
						act(Number(order.shipment_photos) > 0
								? `Yuk rasmi (${order.shipment_photos} ta)`
								: 'Yuk rasmga olinmagan',
							'uil-camera',
							Number(order.shipment_photos) > 0 ? 'btn-outline-success' : 'btn-outline-secondary',
							() => onPhotos(order))}
					{stage === 'ready' && onStall &&
						act(order.stall_reason ? 'Sababni o\'zgartirish' : 'Nega turibdi — sabab yozish',
							'uil-comment-exclamation', 'btn-outline-warning', () => onStall(order))}
				</span>
			</div>
		</div>
	)
}

// Ixcham kartochka. `showPrint` — faqat 1- va 4-ustunda (user talabi).
function KbCard({ order, stage, deptTypes, onAdvance, onShip, onPrint, onTiming, showPrint, showRoll, hideWork, onPause, onResume, onTimeline, onLateReason }) {
	const u = getUrgency(order.delivery_date)
	const urg = URGENCY[u]
	// `relativeDay(cheque_opened_at)` endi kerak emas — chek ochilgan SOAT o'rniga
	// jami o'tgan vaqt ko'rsatiladi (`totalSeconds`), u ustaga foydaliroq.
	const depts = (order.departments || []).filter(d =>
		!deptTypes || deptTypes.includes(Number(d.product_type)))
	/* Chiqarib yuborish mumkinmi. `departments` faqat ishlab chiqarish turlarini
	   (3,4,5) qaytaradi — Produkt/Rulon chekida u BO'SH bo'ladi. Faqat `dept_ready`
	   ga qarasak bunday chek «Тайёр» ustunida turib, tugmasi abadiy o'chiq qolardi
	   (masalan #6821). `warehouse_ready` aynan shu holat uchun: ishlab chiqarish
	   kerak bo'lmagan chek darhol chiqarishga tayyor. Backend `ship()` bo'sh
	   product_types ni allaqachon qo'llab-quvvatlaydi. */
	const shipDepts = (order.departments || []).some(d => d.dept_ready) || !!order.warehouse_ready
	const dueDay = dayWord(order.delivery_date)
	const shipDay = dayWord(order.shipped_at)
	// Ish hajmi — faqat bitta bo'lim ko'rinayotganda (ombor uchun chiplarda beriladi)
	// Omborda ish hajmi va ketgan vaqt ko'rsatilmaydi (user qarori): omborchi
	// ishlab chiqarmaydi, uning ishi — chiqarib yuborish.
	const amount = !hideWork && stage !== 'shipped' && depts.length === 1 ? deptAmount(depts[0]) : null
	const total = hideWork ? null : totalSeconds(order)
	// Bosqich nomi kartochkada ko'rinsin. Bir nechta bo'lim bo'lsa (ombor)
	// yagona nom yo'q — o'shanda chiplar allaqachon har birini ko'rsatadi.
	const st = depts.length === 1 ? depts[0] : null

	// TO'XTATILGAN buyurtma YIG'ILGAN holda chiziladi: bir qator yozuv,
	// tugmasiz. Usta uni ko'radi (nima bo'lganini biladi), lekin u
	// ustunni to'ldirib turmaydi. Davom ettirishni SOTUVCHI qiladi -
	// kim to'xtatgan bo'lsa, o'sha ochadi.
	if (order.is_paused && stage !== 'shipped') {
		return (
			<div className="kb-card is-paused" onClick={() => onTiming && onTiming(order)}>
				<div className="kb-pz-top">
					<i className="uil-pause-circle" />
					<span className="kb-pz-no">#{chequeNo(order)}</span>
					<span className="kb-pz-client">{order.client_name}</span>
				</div>
				<div className="kb-pz-note">
					{order.paused_source === 'seller' ? 'SOTUVCHI to\'xtatdi' : 'Vaqtincha to\'xtatildi'}
					{order.paused_by && <b> · {order.paused_by}</b>}
				</div>
				{order.pause_reason && <div className="kb-pz-why">{order.pause_reason}</div>}
				{/* DAVOM ETTIRISH — faqat TO'XTATGAN tomonda.
				    Sotuvchi to'xtatgan ishni usta o'zicha ocholmaydi: aks holda
				    «men aytmagandim» degan eski muammo qaytadi. */}
				{onResume && order.paused_source !== 'seller' ? (
					<button className="kb-pz-go" onClick={(e) => { e.stopPropagation(); onResume(order) }}>
						<i className="uil-play" /> Davom ettirish
					</button>
				) : order.paused_source === 'seller' ? (
					<div className="kb-pz-wait">
						<i className="uil-lock-alt" /> Sotuvchi ochishi kerak
					</div>
				) : null}
			</div>
		)
	}

	return (
		/* Kartochkaning O'ZI bosilsa bosqich vaqtlari ochiladi. Tugmalar ichida
		   `stopPropagation` bor, shuning uchun ular tasodifan oynani ochmaydi. */
		<div className={'kb-card' + (order.is_urgent && stage !== 'shipped' ? ' is-urgent' : '')
				+ (onTiming ? ' is-clickable' : '')}
			style={{ '--stripe': stage === 'shipped' ? '#059669' : urg.accent }}
			onClick={() => onTiming && onTiming(order)}>
			{/* Chek raqami va mijoz BITTA qatorda — ilgari ikki qator edi. Ustunga
			    ko'proq zakaz sig'ishi uchun (user talabi: 5 ta ko'rinsin). */}
			<div className="kb-card-top">
				<span className="kb-no"><i>#</i>{chequeNo(order)}</span>
				<span className="kb-client">
					{order.client_name}
					{hideWork && order.client_phone && <span className="kb-phone">{order.client_phone}</span>}
					{showRoll && st?.roll_name && (
						<span className="kb-roll" title={"G'altak " + (st.balance_id || '')}>
							<i className="uil-layer-group" />{st.roll_name}
						</span>
					)}
				</span>
				{/* Yorliq: zarur bo'lsa ZARUR, aks holda BOSQICH NOMI (Yangi Buyurtma /
				    Jarayonda / Buyurtma Tayyor) o'z rangida. Shoshilinchlik baribir
				    ko'rinib turadi — vada bloki va kartochka chizig'i o'sha rangda. */}
				{stage === 'shipped'
					? <span className="kb-tag is-done">{order.fully_closed ? "To'liq" : 'Qisman'}</span>
					: order.is_urgent
						? <span className="kb-tag is-urgent"><i className="uil-bolt" /> ZARUR</span>
						/* KECHIKKAN bosqich nomidan USTUN: zakaz tayyor bo'lsa ham vada
						   o'tib ketgani muhimroq — «Буюртма Тайёр» deb yozib qo'ysak
						   kechikkani ko'rinmay qolardi. */
						: u === 'danger'
							? <span className="kb-tag" style={{ background: urg.accent }}>{urg.label}</span>
							: st
								? <span className="kb-tag" style={{ background: st.status_color || urg.accent }}>{st.status_name}</span>
								: <span className="kb-tag" style={{ background: urg.accent }}>{urg.label}</span>}
			</div>


			{/* VADA — sexdan uzoqdan ko'rinishi kerak, shuning uchun vaqt yirik
			    va shoshilinchlik rangida (user talabi: «kattaroq qilish kerak»). */}
			{/* Kun so'zi FAQAT bugundan farq qilsa yoziladi. Tepadagi filtr allaqachon
			    «Bugun» deb turadi — har kartochkada takrorlash shovqin bo'lardi. */}
			{stage === 'shipped' ? (
				<div className="kb-when" style={{ '--u': '#059669' }}>
					<span className="kb-when-main">
						<i className="uil-truck" />
						{shipDay && shipDay !== 'Bugun' && <span className="kb-when-d">{shipDay}</span>}
						<span className="kb-when-t">{order.shipped_at ? formatDate(order.shipped_at, 'HH:mm') : '—'}</span>
					</span>
					{total !== null && <span className="kb-total" title="Boshidan oxirigacha ketgan vaqt">
						<i className="uil-stopwatch" />{shortDur(total)}
					</span>}
				</div>
			) : hideWork ? (
				/* OMBOR: vada emas, TAYYOR bo'lgandan beri o'tgan vaqt. Omborchi
				   uni darhol chiqarishi kerak, vada esa sotuvchining ishi. */
				(() => {
					const wsec = order.ready_at ? Math.max(0, (Date.now() - new Date(order.ready_at)) / 1000) : null
					const lv = waitLevel(wsec)
					if (!lv) return <div className="kb-when is-none"><i className="uil-clock" />Vaqt yo'q</div>
					return (
						<div className="kb-when" style={{ '--u': lv.color }}>
							<span className="kb-when-main">
								<span className="kb-when-d">{lv.label}</span>
								<span className="kb-when-t">{shortDur(wsec)}</span>
							</span>
						</div>
					)
				})()
			) : order.delivery_date ? (
				/* ISH HAJMI va VADA bitta qatorda — ilgari ikki qator edi va kartochka
				   ortiqcha baland chiqardi. Metr chapda, vaqt MARKAZDA (o'ng ustun
				   bo'sh qoladi, shuning uchun markaz saqlanadi). */
				<div className="kb-when" style={{ '--u': urg.accent }}>
					{amount && <span className="kb-amount">{amount}</span>}
					<span className="kb-when-main">
						{dueDay && dueDay !== 'Bugun' && <span className="kb-when-d">{dueDay}</span>}
						<span className="kb-when-t">{formatDate(order.delivery_date, 'HH:mm')}</span>
					</span>
					{total !== null && <span className="kb-total" title="Chek yozilganidan beri o'tgan vaqt">
						<i className="uil-stopwatch" />{shortDur(total)}
					</span>}
				</div>
			) : (
				<div className="kb-when is-none">
					{amount && <span className="kb-amount">{amount}</span>}
					<span className="kb-when-main"><i className="uil-clock" />Vada yo'q</span>
					{total !== null && <span className="kb-total"><i className="uil-stopwatch" />{shortDur(total)}</span>}
				</div>
			)}

			{/* Ko'p bo'limli foydalanuvchida (ombor) qaysi sex ekani ko'rinsin */}
			{(!deptTypes || deptTypes.length > 1) && stage !== 'shipped' && (
				depts.length > 0 ? (
					<div className="kb-chips">
						{depts.map((d, i) => (
							<span key={i} className={'kb-chip' + (d.dept_ready ? ' is-green' : '')}>
								{d.type_name}{!hideWork && deptAmount(d) && <b> {deptAmount(d)}</b>}
							</span>
						))}
					</div>
				) : order.all_type_names ? (
					/* Ishlab chiqarishdan o'tmaydigan chek (Produkt/Rulon) — to'g'ridan-to'g'ri
					   ombordan beriladi. Ilgari bu yerda BO'SH joy turardi. */
					<div className="kb-chips">
						<span className="kb-chip is-stock">
							<i className="uil-box me-1" />{order.all_type_names} · ombordan
						</span>
					</div>
				) : null
			)}

			{/* IXCHAM AMAL QATORI - belgi tugmalar, matnsiz.
			    Kartochka sexda uzoqdan ko'riladi: asosiy joy VADA va BOSQICH
			    tugmasiga tegishli. Bu qator ularni siqib qo'ymasligi kerak.
			    «YO'LI» CHIQARIB YUBORILGANDA HAM turadi: chekning yo'li aynan
			    o'shanda TUGAYDI, ya'ni «qayerda kechikdi» degan savol ham
			    o'shanda beriladi. To'xtatish/sabab esa tugagan ishda ma'nosiz. */}
			{(onPause || onLateReason || onTimeline) && (
				<div className="kb-tools">
					{stage !== 'shipped' && onLateReason && getUrgency(order.delivery_date) === 'danger' && (
						<button className={'kb-tool ' + (order.late_reason ? 'has-reason' : 'need-reason')}
							title={order.late_reason ? lateReasonLabel(order.late_reason) : 'Kechikish sababini yozing'}
							onClick={(e) => { e.stopPropagation(); onLateReason(order) }}>
							<i className={order.late_reason ? 'uil-info-circle' : 'uil-question-circle'} />
							{order.late_reason ? lateReasonLabel(order.late_reason) : 'Sabab'}
						</button>
					)}
					<span className="kb-tools-sep" />
					{onTimeline && (
						<button className="kb-tool is-icon" title="Chek yo'li"
							onClick={(e) => { e.stopPropagation(); onTimeline(order) }}>
							<i className="uil-clock-three" />
						</button>
					)}
					{stage !== 'shipped' && onPause && (
						<button className="kb-tool is-icon" title="Vaqtincha to'xtatish"
							onClick={(e) => { e.stopPropagation(); onPause(order) }}>
							<i className="uil-pause" />
						</button>
					)}
				</div>
			)}

			{/* AMAL QATORI — bosqich tugmasi va chek chiqarish YONMA-YON.
			    Ilgari ustma-ust turardi va usta chek chiqaraman deb adashib
			    «Jarayonga olish» ni bosib yuborishi mumkin edi. Chek tugmasi
			    endi tor, belgi bilan va chetda — tasodifan bosilmaydi. */}
			{(() => {
				// To'xtatilgan buyurtmada bosqich tugmasi BERILMAYDI — aks holda usta
				// to'xtatilganini bilmasdan davom ettiradi va to'xtatish ma'nosiz bo'ladi.
				const advBtns = (onAdvance && !order.is_paused) ? depts.filter(d => !d.dept_ready && d.next_status_id) : []
				// `onShip` bo'lsa chek tugmasi o'sha qatorda chiziladi — bu yerda TAKRORLAMAYMIZ
				const printHere = showPrint && onPrint && !onShip
				if (!advBtns.length && !printHere) return null
				return (
					<div className="kb-actions">
						{advBtns.map((d, i) => (
							<button key={i} className={'kb-btn ' + (d.is_initial ? 'is-start' : 'is-ready')}
								onClick={(e) => { e.stopPropagation(); onAdvance(order.id, Number(d.product_type), d.next_status_id, d.is_initial ? null : order) }}>
								<i className={d.is_initial ? 'uil-play' : 'uil-check'} />
								{d.is_initial ? 'Jarayonga olish' : 'Tayyor'}
								{depts.length > 1 && <em>{d.type_name}</em>}
							</button>
						))}
						{printHere && (
							<button className="kb-print" title="Chek chiqarish (80mm)"
								onClick={(e) => { e.stopPropagation(); onPrint(order, false) }}>
								<i className="uil-print" />
								<span>Chek</span>
							</button>
						)}
					</div>
				)
			})()}

			{/* Ombor: chiqarib yuborish va chek YONMA-YON — boshqa bo'limlardagidek */}
			{onShip && (
				<div className="kb-actions">
					<button className={'kb-btn ' + (shipDepts ? 'is-ship' : 'is-wait')}
						disabled={!shipDepts} onClick={(e) => { e.stopPropagation(); shipDepts && onShip(order) }}>
						<i className={shipDepts ? 'uil-truck' : 'uil-clock'} />
						{shipDepts ? 'Chiqarib yuborish' : 'Kutilmoqda'}
					</button>
					{onPrint && (
						<button className="kb-print" title="Chek chiqarish (80mm)"
							onClick={(e) => { e.stopPropagation(); onPrint(order, false) }}>
							<i className="uil-print" /><span>Chek</span>
						</button>
					)}
				</div>
			)}

		</div>
	)
}

// Kartochka uchun QISQA davomiylik: «45d», «2s 45d», «3k 4s».
// ⚠️ «2:45» shakli ishlatilmadi — yonida vada VAQTI turadi (23:00) va ikkalasi
// soatday o'qilib chalkashardi. Harflar bilan yozilganda aralashmaydi.
function shortDur(sec) {
	const s = Math.max(0, Math.round(Number(sec) || 0))
	const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60)
	if (d > 0) return h > 0 ? `${d}k ${h}s` : `${d}k`
	if (h > 0) return m > 0 ? `${h}s ${m}d` : `${h}s`
	return `${Math.max(1, m)}d`
}

// Chek boshidan hozirgacha (yoki chiqib ketguncha) o'tgan vaqt
function totalSeconds(order) {
	const from = order.cheque_opened_at
	if (!from) return null
	const end = order.shipped_at || order.cheque_closed_at
	const sec = ((end ? new Date(end) : new Date()) - new Date(from)) / 1000
	return sec >= 0 ? sec : null
}

// Soniyani o'qishga qulay ko'rinishga: «2 soat 45 daq», «17 daq», «45 son»
function humanDur(sec) {
	const s = Math.max(0, Math.round(Number(sec) || 0))
	if (s < 60) return `${s} son`
	const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60)
	if (d > 0) return h > 0 ? `${d} kun ${h} soat` : `${d} kun`
	if (h > 0) return m > 0 ? `${h} soat ${m} daq` : `${h} soat`
	return `${m} daq`
}

// Bosqich nomiga rang — kengashdagi ustun ranglari bilan bir xil bo'lsin
function stageAccent(name) {
	const n = (name || '').toLowerCase()
	if (n.includes('yangi')) return '#e11d48'
	if (n.includes('jarayon')) return '#d97706'
	if (n.includes('tayyor')) return '#2563eb'
	return '#059669'
}

function StatsBar({ orders, deptType }) {
	const count = orders.length
	let metrText = null
	let qtyText = null

	/* Bo'lim jamlanmasi — kartochkalardagi `departments` blokidan.
	   Bir chekda bir necha bo'lim bo'ladi, shuning uchun HAR DOIM shu bo'lim
	   tanlanadi, chekning jamisi olinmaydi. */
	const sumOf = (field) => orders.reduce((sum, o) => {
		const d = (o.departments || []).find(x => Number(x.product_type) === deptType)
		return sum + Number(d?.[field] || 0)
	}, 0)

	if (deptType === 4) {
		const totalMetr = sumOf('total_metr')
		metrText = totalMetr > 0 ? `${totalMetr.toFixed(1)} m` : null
	} else if (deptType === 3) {
		// Aksessuarda ham SOTUV metri (kartochkalar bilan bir xil bo'lishi shart)
		const totalMetr = sumOf('total_metr')
		metrText = totalMetr > 0 ? `${totalMetr.toFixed(2)} m` : null
	} else if (deptType === 5) {
		const totalKvMetr = sumOf('total_kv_metr')
		metrText = totalKvMetr > 0 ? `${totalKvMetr.toFixed(3)} kv.m` : null
	} else if (deptType != null) {
		const totalQty = sumOf('total_qty')
		qtyText = totalQty > 0 ? `${Math.round(totalQty)} dona` : null
	}

	/* ISH VAQTI — endi HAMMA bo'limda. Ilgari faqat profnastilda chiqardi,
	   ya'ni bu ko'rinishni ochgan rahbar (ROLE_PROD_MANAGER, ixcham rejimni
	   o'chirgan ega/direktor) aksessuar va tunikofon navbatini soatda ko'rmasdi.
	   Usta bu komponentni ko'rmaydi — unda kanban sarlavhasi (kbColMinutes).
	   Model — sana tavsiyasi bilan bir xil (ChequeController::board,
	   estimated_minutes): hajm ÷ sof tezlik + rulon sozlash. */
	const hourText = formatWorkMinutes(deptType != null ? sumOf('estimated_minutes') : 0)

	// KECHIKKAN va TASHLANDIQ — ATAYLAB ikkiga ajratilgan.
	//
	// Ilgari ikkalasi bitta «kechikkan» raqamiga qo'shilardi. Bazada 14 ta
	// kechikkanning yarmi 16-21 kunlik unutilgan cheklar edi (#6784, #6785,
	// #6792, #6809, #6811) — ular status 2 da qotib qolgan. Natijada qizil
	// belgi DOIM yonib turardi va rahbar unga e'tibor bermay qo'ygandi.
	//
	// Endi: 7 kungacha — haqiqiy kechikish (qizil, e'tibor talab qiladi);
	// 7 kundan ortiq — tashlandiq (kulrang, «yopish yoki bekor qilish» ro'yxati).
	const LATE_LIMIT_DAYS = 7
	const now = Date.now()
	let late = 0, abandoned = 0
	orders.forEach(o => {
		if (!o.delivery_date) return
		const diffDays = (now - new Date(o.delivery_date).getTime()) / 86400000
		if (diffDays <= 0) return
		if (diffDays > LATE_LIMIT_DAYS) abandoned++
		else late++
	})

	return (
		<div className="pb-col-stats">
			<span><i className="uil-receipt" />{count} chek</span>
			{metrText && <span><i className="uil-ruler" />{metrText}</span>}
			{qtyText && <span><i className="uil-box" />{qtyText}</span>}
			{hourText && <span title="Navbatdagi ishning taxminiy vaqti"><i className="uil-clock" />{hourText}</span>}
			{late > 0 &&
				<span style={{ color: '#b42318', fontWeight: 600 }} title="Vadasi o'tgan (7 kungacha)">
					<i className="uil-exclamation-triangle" />{late} kechikkan
				</span>}
			{abandoned > 0 &&
				<span style={{ color: '#77819a' }} title="7 kundan ortiq — yopish yoki bekor qilish kerak">
					<i className="uil-archive" />{abandoned} tashlandiq
				</span>}
		</div>
	)
}

// Kunlik reja progress-bar (bugun bajarilgan / reja)
function TargetBar({ target, unit, t }) {
	if (!target || !target.target || target.target <= 0) return null
	const done = Number(target.done_today || 0)
	const goal = Number(target.target || 0)
	const pct = Math.min(100, Math.round((done / goal) * 100))
	const color = pct >= 100 ? '#34d399' : pct >= 70 ? '#60a5fa' : '#f59e0b'
	const fmt = (n) => Number.isInteger(n) ? n : n.toFixed(unit === 'kv.m' ? 2 : 1)
	return (
		<div className="pb-target">
			<div className="pb-target-row">
				<span className="pb-target-l"><i className="uil-bullseye" /> {t('plan_word')}</span>
				<span className="pb-target-v" style={{ color }}>{fmt(done)} / {fmt(goal)} {unit} · {pct}%</span>
			</div>
			<div className="pb-target-track"><div className="pb-target-fill" style={{ width: pct + '%', background: color }} /></div>
		</div>
	)
}

// Bo'lim navbatidagi jami metr (yuklanish uchun)
/**
 * TASHLANDIQ — vadasi 7 kundan ko'proq o'tgan, hech kim tegmagan buyurtma.
 *
 * NEGA AJRATILADI: bazada Profnastilning 32 faol buyurtmasidan 22 tasi shunday
 * edi — ular ustunni to'ldirib, 9 ta TIRIK ishni ko'rinmas qilardi. Yuklanish
 * ham ular bilan hisoblanib 411% chiqardi, tirik ish esa atigi 111% (1.1 kunlik).
 * Ya'ni raqam yolg'on ogohlantirar edi.
 */
const ABANDONED_DAYS = 7

function isAbandoned(order) {
	if (!order.delivery_date) return false
	return (Date.now() - new Date(order.delivery_date).getTime()) > ABANDONED_DAYS * 86400000
}

function deptQueueMetr(orders, deptType) {
	return orders.reduce((sum, o) => {
		const d = (o.departments || []).find(x => Number(x.product_type) === deptType)
		if (!d) return sum
		if (deptType === 4) return sum + Number(d.total_metr || 0)
		if (deptType === 3) return sum + Number(d.total_metr || 0)
		if (deptType === 5) return sum + Number(d.total_kv_metr || 0)
		return sum
	}, 0)
}

// Yuklanish: navbatdagi metr / kunlik quvvat (sotuv 'qancha joy bor'ni ko'radi)
function LoadBar({ queue, capacity, unit }) {
	if (!capacity || capacity <= 0) return null
	const pct = Math.round((queue / capacity) * 100)
	const color = pct >= 100 ? '#e11d48' : pct >= 80 ? '#f59e0b' : '#059669'
	const fmt = (n) => Number.isInteger(n) ? n : n.toFixed(unit === 'kv.m' ? 2 : 1)
	return (
		<div className="pb-target">
			<div className="pb-target-row">
				<span className="pb-target-l"><i className="uil-tachometer-fast-alt" /> Yuklanish</span>
				<span className="pb-target-v" style={{ color }}>
					{fmt(queue)} / {fmt(capacity)} {unit} · {pct}%
					{/* «111%» hech nima demaydi; «1.1 kunlik ish» darhol tushunarli */}
					{queue > 0 && <> · <b>{(queue / capacity).toFixed(1)} kunlik ish</b></>}
				</span>
			</div>
			<div className="pb-target-track"><div className="pb-target-fill" style={{ width: Math.min(100, pct) + '%', background: color }} /></div>
		</div>
	)
}

// Audit hodisasini o'qishga qulay ko'rsatish
function historyLabels(t) {
	return {
		events: { status: t('ev_status'), delivery_date: t('ev_deadline'), dept_stage: t('ev_stage'),
			shipped: t('ev_shipped'), urgent: t('ev_urgent'),
			// Jarayon nazorati hodisalari - «Yo'li» oynasi shularni ham chizadi
			opened: t('ev_opened'), pause: t('ev_pause'), resume: t('ev_resume'),
			late_reason: t('ev_late_reason'), cheque_payment: t('ev_payment') },
		status: { '1': t('st_new'), '2': t('st_process'), '3': t('st_closed') },
		// 'urgent' hodisasi 0/1 yozadi — tarixda «Ha/Yo'q» bo'lib ko'rinsin
		urgent: { '0': t('no'), '1': t('yes') },
	}
}
function renderVal(ev, val, t, lbls) {
	if (val === null || val === undefined || val === '') return t('not_set')
	if (ev === 'urgent') return lbls.urgent[String(val)] || val
	if (ev === 'status' || ev === 'shipped') return lbls.status[String(val)] || val
	if (ev === 'delivery_date') { const d = new Date(val); return isNaN(d) ? val : formatDate(d, 'dd.MM.yyyy HH:mm') }
	// Pul hodisalari xom raqam bo'lib yozilgan — «7143000» o'rniga «7 143 000»
	if (ev === 'cheque_payment' && !isNaN(Number(val))) return formatMoney(val)
	return val
}

function Board() {
	const { t } = useTranslation()

	const userRole = localStorage.getItem('role')
	const storedPT = localStorage.getItem('product_types')
	// ROLE_WORKSHOP (bitta) va ROLE_PROD_MANAGER (bir nechta) — o'z bo'lim(lar)i bilan cheklanadi
	const userProductTypes = ((userRole === 'ROLE_WORKSHOP' || userRole === 'ROLE_PROD_MANAGER') && storedPT)
		? JSON.parse(storedPT)
		: null

	const visibleDepts = userProductTypes
		? DEPARTMENTS.filter(d => userProductTypes.includes(d.type))
		: DEPARTMENTS

	const canAdvance = ['ROLE_WORKSHOP', 'ROLE_PROD_MANAGER'].includes(userRole)

	const isWarehouse = userRole === 'ROLE_WAREHOUSE'
	// Sex va ombor — kanban. ⚠️ Omborchi SOTUVCHI ixchamiga O'TKAZILMAYDI
	// (2026-08-26 xatosi qaytarildi): omborning O'Z jarayoni bor — hudud
	// bo'yicha guruhlash, kutish taymeri, chiqarish oqimi. Ixchamlik uning
	// O'Z ko'rinishi ichida qilinadi (KbRow, pastda).
	const isKanban = ['ROLE_WORKSHOP', 'ROLE_WAREHOUSE'].includes(userRole)
	// Rahbar KUZATADI, bajarmaydi — amal tugmalari va ortiqcha satrlar yashiriladi
	const isManagerView = userRole === 'ROLE_PROD_MANAGER'
	// Ombor barcha bo'limlarni ko'radi, sex — faqat o'zinikini
	const kbDeptTypes = isWarehouse ? null : visibleDepts.map(d => d.type)
	// G'altak bo'yicha guruhlash FAQAT sex bo'limlariga: Profnastil(4), Aksessuar(3),
	// Tunukafon(5) — user talabi. Omborchi hamma bo'limni ko'radi, unda o'rnatish
	// tushunchasi yo'q; ko'p bo'limli foydalanuvchida ham guruh kaliti yagona emas.
	const rollGroupType = !isWarehouse && kbDeptTypes.length === 1 && [3, 4, 5].includes(kbDeptTypes[0])
		? kbDeptTypes[0] : null

	// ── IXCHAM KO'RINISH (2026-08-05) ─────────────────────────────────────────
	// Sotuvchi va rahbariyat uchun. Kartochka 273px edi — 1080p ekranda ustunga
	// 2 tagina zakaz sig'ardi, prodда esa 26 ta zakaz bor. Endi bir qatorda
	// chek raqami + bosqich + vada; hammasi bitta ekranga sig'adi.
	// ⚠️ Usta/omborchi/ishlab chiqarish rahbari bu yerga KIRMAYDI — ular o'z
	//    ko'rinishida qoladi (user talabi: «qogan roldagilarga umuman tegma»).
	const canCompact = ['ROLE_CASHIER', 'ROLE_OWNER', 'ROLE_DIRECTOR'].includes(userRole)
	const [compactMode, setCompactMode] = useState(() => {
		try { return localStorage.getItem('pb_compact') !== '0' } catch (e) { return true }
	})
	const useCompact = canCompact && compactMode

	const [activeOrders, setActiveOrders]       = useState([])
	const [completedOrders, setCompletedOrders] = useState([])
	const [targets, setTargets]                 = useState({})
	const [isOffline, setIsOffline]             = useState(false)
	const [timing, setTiming]                   = useState({ show: false, no: '', client: '', data: null })
	const [lastUpdated, setLastUpdated]         = useState(null)
	const [isFullscreen, setIsFullscreen]       = useState(false)
	const [printData, setPrintData]             = useState({})
	const [soundEnabled, setSoundEnabled]       = useState(true)
	const [shipModal, setShipModal]             = useState({ show: false, order: null, selected: [] })
	// Tashlandiq ustun bo'yicha ochib-yopiladi (sukut — yopiq)
	const [showAbandoned, setShowAbandoned]     = useState({})
	const [sortMode, setSortMode]               = useState('vada')
	// Chek jarayoni nazorati (2026-08-01): to'xtatish, yo'l, kechikish sababi
	const [tlModal, setTlModal]                 = useState({ show: false, order: null, data: null, loading: false })
	const [lateModal, setLateModal]             = useState({ show: false, order: null, reason: '', note: '' })
	const [lateReasons, setLateReasons]         = useState([])
	/* Omborda turib qolish sababi (2026-08-26) — lateModal naqshi bilan */
	const [stallModal, setStallModal]           = useState({ show: false, order: null, reason: '', note: '' })
	const [stallReasons, setStallReasons]       = useState([])
	/* YUK RASMLARI (2026-09-01) — chiqib ketgan yukning Telegramdagi rasmi */
	const [photoModal, setPhotoModal]           = useState({ show: false, order: null, list: [], loading: false })
	const printChequeRef                        = useRef(null)
	const prevOrderIds                          = useRef(null)   // null = birinchi yuklama
	/* ⚠️ BIRINCHI SO'ROV TUGADIMI (2026-08-13, foydalanuvchi topdi).
	   Muammo: `activeOrders` bo'sh massivdan boshlanadi. Effekt sahifa
	   ochilishi bilan BO'SH ro'yxat ustida ishlab, «birinchi yuklama»
	   himoyasini (`prevOrderIds === null`) o'sha yerda sarflab yuborardi.
	   Keyin ma'lumot kelganda 42 ta zakazning HAMMASI «yangi» bo'lib
	   chiqib, kengashga kirgan zahoti ovoz boshlanardi.
	   Endi himoya so'rov TUGAGUNICHA kutadi. */
	const boardLoaded                           = useRef(false)
	const prevUrgentIds                         = useRef(null)   // zarurga O'TGANini aniqlash uchun
	const prevReadyIds                          = useRef(null)   // ombor uchun: TAYYOR bo'lganini aniqlash

	const handlePrint = useReactToPrint({ content: () => printChequeRef.current })

	async function printCheque(order, isA4 = false) {
		const response = await GET(`/cheques/${order.id}`, {}, { loader: true })
		if (httpOk(response)) {
			setPrintData({
				...response.data,
				a4: isA4,
				// Sex monobloki — ISH TOPSHIRIG'I: faqat o'z bo'limi, narxsiz.
				// Ombor/rahbar/direktor/kassirda `work_dept` yo'q — ular to'liq chek oladi.
				work_dept: userRole === 'ROLE_WORKSHOP' ? visibleDepts.map(d => d.type) : null,
				// Ombor — to'liq chek + haydovchi shu chek uchun qancha olib kelishi
				driver_note: isWarehouse,
			})
			setTimeout(() => handlePrint(), 100)
		}
	}

	// ---- Chek jarayoni nazorati -------------------------------------------
	/* SEXDA SABAB SO'RALADI, sotuvchida esa yo'q.
	   Farqi bor: sotuvchi «mijoz keyinroq oladi» deb bir bosadi, sex esa
	   TEXNIK sababdan to'xtatadi (material yetmadi, stanok buzildi) — aynan
	   shu sabablar rahbarga kerak, chunki ular takrorlanadi. */
	const [pauseModal, setPauseModal] = useState({ show: false, order: null, reason: '' })

	async function doPause() {
		const o = pauseModal.order
		if (!o) return
		const res = await POST(`/cheques/${o.id}/pause`,
			{ reason: pauseModal.reason.trim(), source: 'workshop' }, { loader: true })
		if (httpOk(res)) {
			setPauseModal({ show: false, order: null, reason: '' })
			fetchData()
		}
	}

	async function doResume(order) {
		const res = await POST(`/cheques/${order.id}/resume`, { source: 'workshop' }, { loader: true })
		if (httpOk(res)) fetchData()
	}

	async function openTimeline(order) {
		setTlModal({ show: true, order, data: null, loading: true })
		const res = await GET(`/cheques/${order.id}/timeline`, {}, { loader: false })
		setTlModal(p => ({ ...p, data: httpOk(res) ? res.data : null, loading: false }))
	}

	async function openLate(order) {
		if (lateReasons.length === 0) {
			const r = await GET('/late-reasons', {}, { loader: false })
			if (httpOk(r)) setLateReasons(r.data)
		}
		setLateModal({ show: true, order, reason: order.late_reason || '', note: '' })
	}

	async function saveLate() {
		const o = lateModal.order
		if (!o || !lateModal.reason) return
		const res = await PUT(`/cheques/${o.id}/late-reason`,
			{ reason: lateModal.reason, note: lateModal.note }, { loader: true })
		if (httpOk(res)) { setLateModal({ show: false, order: null, reason: '', note: '' }); fetchData() }
	}

	/* ═══ OMBORDA TURIB QOLISH SABABI (2026-08-26) ═══
	   Naqsh openLate/saveLate bilan bir xil; farqi — sababni OLIB TASHLASH
	   ham mumkin (buyurtma harakatga kelganda). */
	/* Yuk rasmlarini ochish — rasm bazadan base64 bo'lib keladi (ochiq URL yo'q,
	   chunki rasmda mijoz, haydovchi va mashina raqami ko'rinadi). */
	async function openPhotos(order) {
		setPhotoModal({ show: true, order, list: [], loading: true })
		const r = await GET(`/cheques/${order.id}/shipment-photos`, {}, { loader: false })
		setPhotoModal({ show: true, order, list: httpOk(r) ? (r.data || []) : [], loading: false })
	}

	async function openStall(order) {
		if (stallReasons.length === 0) {
			const r = await GET('/stall-reasons', {}, { loader: false })
			if (httpOk(r)) setStallReasons(r.data)
		}
		setStallModal({ show: true, order, reason: order.stall_reason || '', note: order.stall_note || '' })
	}

	async function saveStall(clear = false) {
		const o = stallModal.order
		if (!o) return
		if (!clear && !stallModal.reason) return
		const res = await PUT(`/cheques/${o.id}/stall-reason`,
			{ reason: clear ? '' : stallModal.reason, note: clear ? '' : stallModal.note }, { loader: true })
		if (httpOk(res)) { setStallModal({ show: false, order: null, reason: '', note: '' }); fetchData() }
	}


	/* Yangi/Jarayonda ustunlarida haydovchi SO'RALMAYDI (egasining talabi:
	   «tayyorda tursa yetadi») — chek to'g'ridan-to'g'ri chiqadi. */
	async function printShipDocDirect(order) {
		const res = await GET(`/cheques/${order.id}`, {}, { loader: true })
		if (!httpOk(res)) return
		setPrintData({ ...res.data, shipment: true, a4: false, work_dept: null })
		setTimeout(() => handlePrint(), 100)
	}


	const fetchData = useCallback(async () => {
		// Kengash SANAGA BOG'LIQ EMAS (user qarori 2026-08-05): faol ustunlar
		// tugallanmagan ishni doim ko'rsatadi, «Chiqib ketganlar» esa o'z 3
		// kunlik oynasiga ega. Shu sabab hech qanday sana/filtr parametri yo'q.
		const params = {}
		try {
			const response = await GET('/board', params, {})
			if (httpOk(response)) {
				// Ovoz himoyasi shu bayroqni kutadi (izoh: `boardLoaded`).
				// setState'dan OLDIN qo'yiladi — effekt qayta chizishdan
				// keyin ishlaydi va bayroqni allaqachon ko'radi.
				boardLoaded.current = true
				setActiveOrders(response.data.active || [])
				setCompletedOrders(response.data.completed || [])
				setTargets(response.data.targets || {})
				setLastUpdated(new Date())
				setIsOffline(false)          // aloqa tiklandi
			} else {
				setIsOffline(true)           // server javob bermadi — eski ma'lumot
			}
		} catch {
			setIsOffline(true)               // tarmoq uzildi
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// Kartochka bosilganda — chek qaysi bosqichda qancha turgani.
	// Bo'lim aniq bo'lsa (sex foydalanuvchisi) shuni yuboramiz, aks holda
	// (ombor) butun chek bo'yicha ko'rsatiladi.
	async function openTiming(order) {
		const pt = kbDeptTypes && kbDeptTypes.length === 1 ? kbDeptTypes[0] : null
		const res = await GET(`/cheques/${order.id}/stage-timing`, pt ? { product_type: pt } : {}, { loader: true })
		if (httpOk(res)) {
			setTiming({ show: true, no: chequeNo(order), client: order.client_name, data: res.data })
		}
	}

	useEffect(() => {
		fetchData()
		const interval = setInterval(fetchData, 30000)
		return () => clearInterval(interval)
	}, [fetchData])

	useEffect(() => {
		const handler = () => setIsFullscreen(!!document.fullscreenElement)
		document.addEventListener('fullscreenchange', handler)
		return () => document.removeEventListener('fullscreenchange', handler)
	}, [])

	// Yangi chek aniqlash va e'lon qilish
	useEffect(() => {
		// Ma'lumot hali kelmagan — bo'sh ro'yxatda taqqoslash ma'nosiz va
		// «birinchi yuklama» himoyasini bekorga sarflaydi (izoh: `boardLoaded`).
		if (!boardLoaded.current) return

		const currentIds = new Set(activeOrders.map(o => o.id))
		const urgentIds = new Set(activeOrders.filter(o => o.is_urgent).map(o => o.id))
		// Omborchi uchun signal — zakaz TAYYOR bo'lishi, yozilishi emas
		const readyIds = new Set(activeOrders
			.filter(o => kbStageOf(o, kbDeptTypes, isWarehouse) === 'ready').map(o => o.id))
		if (prevOrderIds.current === null) {
			prevOrderIds.current = currentIds
			prevUrgentIds.current = urgentIds
			prevReadyIds.current = readyIds
			return
		}
		if (soundEnabled && isWarehouse) {
			/* OMBOR: «Zakaz tayyor!» — ishi shundan boshlanadi. Ilgari har yangi
			   zakaz e'lon qilinardi (42 ta), holbuki omborchi ularning atigi 8 tasi
			   bilan ishlaydi — qolgani shovqin bo'lib, ovoz o'chirib qo'yilardi.
			   `zakaz-tayyor` fayli hali yozilmagan bo'lsa jim o'tkaziladi va faqat
			   chek raqami aytiladi (yo'q bo'lak e'lonni buzmaydi). */
			activeOrders
				.filter(o => readyIds.has(o.id) && !(prevReadyIds.current || new Set()).has(o.id))
				.forEach(o => {
					const no = o.parent_id || o.id
					announce(['zakaz-tayyor', 'chek-raqami', ...numberToClips(no)],
						`Zakaz tayyor! Chek raqami ${numberToUzbekWords(no)}`)
				})
		}
		if (soundEnabled && !isWarehouse) {
			// FAQAT O'Z BO'LIMI. `activeOrders` da hamma bo'limning zakazi keladi
			// (filtr ustunlarni chizishda ishlaydi), shuning uchun Profnastil
			// monoblokida aksessuar zakazi ham e'lon qilinardi.
			// Ombor (kbDeptTypes null) hammasini eshitadi — u hamma bo'limni chiqaradi.
			const isMine = (o) => !kbDeptTypes
				|| (o.departments || []).some(d => kbDeptTypes.includes(Number(d.product_type)))

			// MAVJUD zakaz zarurga o'tgan bo'lsa ham e'lon qilinadi: sotuvchi belgilagach
			// usta darhol bilishi kerak, aks holda borib aytishga majbur bo'ladi.
			// Yangi kelganlar bu ro'yxatga tushmaydi — ular quyida bir marta aytiladi.
			activeOrders
				.filter(o => isMine(o)
					&& o.is_urgent
					&& prevOrderIds.current.has(o.id)
					&& !(prevUrgentIds.current || new Set()).has(o.id))
				.forEach(o => {
					const no = o.parent_id || o.id
					announce(['zarur-zakaz', 'chek-raqami', ...numberToClips(no),
						...(o.parent_id ? [(o.cheque_suffix || '').toLowerCase()] : [])],
						`Zarur zakaz! Chek raqami ${numberToUzbekWords(no)}`)
				})

			activeOrders
				.filter(o => isMine(o) && !prevOrderIds.current.has(o.id))
				.forEach(o => {
					// Zarur zakaz boshqacha e'lon qilinadi — usta ekranga qaramasdan biladi
					const head = o.is_urgent ? 'zarur-zakaz' : (o.parent_id ? 'qoshimcha-zakaz' : 'yangi-zakaz')
					const headText = o.is_urgent ? 'Zarur zakaz!' : (o.parent_id ? "Qo'shimcha zakaz!" : 'Yangi zakaz!')
					if (o.parent_id) {
						announce(
							[head, 'chek-raqami', ...numberToClips(o.parent_id), (o.cheque_suffix || '').toLowerCase()],
							`${headText} Chek raqami ${numberToUzbekWords(o.parent_id)} ${o.cheque_suffix}`)
					} else {
						announce(
							[head, 'chek-raqami', ...numberToClips(o.id)],
							`${headText} Chek raqami ${numberToUzbekWords(o.id)}`)
					}
				})
		}
		prevOrderIds.current = currentIds
		prevUrgentIds.current = urgentIds
		prevReadyIds.current = readyIds
	}, [activeOrders]) // eslint-disable-line

	function toggleFullscreen() {
		if (!document.fullscreenElement) document.documentElement.requestFullscreen()
		else document.exitFullscreen()
	}

	function openShipModal(order) {
		// Faqat tayyor bo'limlar oldindan tanlanadi
		const ready = (order.departments || []).filter(d => d.dept_ready).map(d => Number(d.product_type))
		setShipModal({
			show: true, order, selected: ready,
			// Haydovchi bloki (2026-08-26): chiqarishda MAJBURIY (yoki «mijoz o'zi»)
			mode: 'own', own: [], recent: [],
			name: order.driver_name || '', phone: '', car: order.driver_car || '',
			selfPickup: false,
		})
		// Ro'yxatlar + telefon fonda yuklanadi — oyna kutdirmasin
		Promise.all([
			GET('/drivers', {}, { loader: false }),
			GET('/recent-drivers', {}, { loader: false }),
			GET(`/cheques/${order.id}`, {}, { loader: false }),
		]).then(([own, dr, ch]) => {
			setShipModal(p => {
				if (!p.show || p.order?.id !== order.id) return p
				const ownList = httpOk(own) ? (own.data || []) : []
				return {
					...p,
					own: ownList,
					recent: httpOk(dr) ? (dr.data || []) : [],
					// Saqlangan telefon yo'qolmasin (PUT hammasini qayta yozadi)
					phone: p.phone || (httpOk(ch) ? (ch.data.driver_phone || '') : ''),
					mode: ownList.length > 0 ? 'own' : 'street',
				}
			})
		})
	}

	function toggleShipDept(type) {
		setShipModal(m => ({
			...m,
			selected: m.selected.includes(type) ? m.selected.filter(x => x !== type) : [...m.selected, type],
		}))
	}

	async function confirmShip() {
		const { order, selected } = shipModal
		if (!order) return
		// Ishlab chiqarishsiz chek (Produkt/Rulon) — bo'lim ro'yxati bo'sh bo'ladi.
		// Backend `product_types` yuborilmasa BUTUN chekni chiqaradi, shuning uchun
		// bunday chekda tanlov talab qilmaymiz.
		const noDepts = (order.departments || []).length === 0
		if (!noDepts && selected.length === 0) return

		/* ═══ HAYDOVCHI MAJBURIY (2026-08-26, egasining qarori) ═══
		   90% pulni haydovchi olib keladi — kim ekani yozilmasdan tovar
		   chiqmasin. Yagona istisno — «Mijoz o'zi olib ketadi». */
		if (!shipModal.selfPickup && !String(shipModal.name || '').trim()) {
			toast.error("Haydovchini tanlang yoki «Mijoz o'zi olib ketadi»ni belgilang")
			return
		}
		if (!shipModal.selfPickup) {
			await PUT(`/cheques/${order.id}/driver`, {
				driver_name: shipModal.name, driver_phone: shipModal.phone, driver_car: shipModal.car,
			}, { loader: false })
		}

		const response = await POST(`/board/ship/${order.id}`,
			noDepts ? {} : { product_types: selected }, { loader: true })
		if (httpOk(response)) {
			toast.success(response.data?.closed ? "Chek to'liq chiqarildi" : "Tanlangan bo'limlar chiqarildi")
			const wasSelf = shipModal.selfPickup
			setShipModal({ show: false, order: null, selected: [] })
			fetchData()

			/* YUK XATI O'ZI CHIQADI — tovar hujjatsiz ketmasin (alohida tugma
			   bosish shart emas). «Mijoz o'zi»da haydovchi qatorlari bo'sh. */
			const chRes = await GET(`/cheques/${order.id}`, {}, { loader: false })
			if (httpOk(chRes)) {
				setPrintData({
					...chRes.data,
					...(wasSelf ? { driver_name: null, driver_phone: null, driver_car: null } : {}),
					shipment: true, a4: false, work_dept: null,
				})
				setTimeout(() => handlePrint(), 100)
			}
		}
	}

	// `finishOrder` — «Tayyor» tugmasi bosilganda beriladi (bosqich yakuni).
	// Vadadan oldin ulgursa bayram, kech qolsa xafa stiker.
	async function advanceStage(orderId, productType, nextStatusId, finishOrder = null) {
		const response = await PUT(`/cheques-products-status`, { id: orderId, status: nextStatusId, product_type: productType }, { loader: true })
		if (httpOk(response)) {
			if (finishOrder) {
				const due = finishOrder.delivery_date ? new Date(finishOrder.delivery_date).getTime() : null
				const late = due !== null ? Date.now() - due : 0
				if (due === null || late <= 0) {
					fireConfetti()
					playEffect('chapak')
					toast.success('Vaqtida bajarildi!')
				} else if (late > LATE_TOLERANCE) {
					dropSadSticker()
					toast.warning('Kechikib bajarildi')
				} else {
					toast.success('Bosqich oldinga surildi')
				}
			} else {
				toast.success('Bosqich oldinga surildi')
			}
			fetchData()
		}
	}

	/**
	 * Bo'lim buyurtmalari — TIRIK va TASHLANDIQ alohida.
	 *
	 * Tashlandiq sukut bo'yicha YASHIRIN: ular ustunni to'ldirib, haqiqiy ishni
	 * ko'rinmas qilardi (Profnastilda 22 o'lik / 9 tirik). Ustun sarlavhasidagi
	 * tugma bilan ochiladi.
	 */
	function getDeptOrders(productType) {
		const all = activeOrders.filter(o =>
			(o.departments || []).some(d => Number(d.product_type) === productType && !d.dept_ready)
		)
		const tirik = sortOrders(all.filter(o => !isAbandoned(o)), sortMode)
		const eski  = sortOrders(all.filter(isAbandoned), sortMode)
		return { tirik, eski }
	}

	const warehouseOrders = sortOrders(
		activeOrders.filter(o => o.warehouse_ready || (o.departments || []).some(d => d.dept_ready))
	)

	const boardContent = (
		<>
		<style>{PB_CSS}</style>
		{/* `pb-fit` — FAQAT ixcham ko'rinishda. `.pb` da `height:100vh` bor, lekin
		    u sahifa sarlavhasi OSTIDA turadi va ekrandan sarlavha balandligicha
		    (o'lchandi: 70px) oshib ketadi — pastdagi «Чиқиб кетганлар» kesiladi.
		    Ixcham ko'rinishda balandlikni `BoardCompact` o'zi o'lchaydi, shuning
		    uchun bu yerda `height:auto` qilinadi. Boshqa rollarda klass qo'shilmaydi. */}
		<div className={'pb' + (useCompact ? ' pb-fit' : '')}>

			{/* Topbar */}
			<div className="pb-top">
				<div className="pb-title">
					<span className="pb-live"><span className="pb-live-dot" />LIVE</span>
					<h5>{t('production_board')}</h5>
					<span className="pb-count">{activeOrders.filter(o => !o.warehouse_ready).length}</span>
				</div>

				<div className="pb-controls">
					{/* Ixcham / Kartochka — tanlov brauzerda saqlanadi */}
					{canCompact &&
						<div className="pb-seg">
							<button className={compactMode ? 'on' : ''}
								onClick={() => { setCompactMode(true); try { localStorage.setItem('pb_compact', '1') } catch (e) { } }}>
								<i className="uil-list-ul me-1" />Ихчам
							</button>
							<button className={compactMode ? '' : 'on'}
								onClick={() => { setCompactMode(false); try { localStorage.setItem('pb_compact', '0') } catch (e) { } }}>
								<i className="uil-apps me-1" />Карточка
							</button>
						</div>}
					{/* Saralash — faqat RAHBARDA. Ustada vada bo'yicha tartib tabiiy;
					    rahbarga esa tashlandiqni tozalashda «eng eski» qulay. */}
					{isManagerView &&
						<div className="pb-seg">
							<button className={sortMode === 'vada' ? 'on' : ''} onClick={() => setSortMode('vada')}>Vada bo'yicha</button>
							<button className={sortMode === 'eski' ? 'on' : ''} onClick={() => setSortMode('eski')}>Eng eski</button>
						</div>}
					{lastUpdated && <BoardClock lastUpdated={lastUpdated} />}
					<button className={'pb-ico ' + (soundEnabled ? 'is-on' : '')} title={soundEnabled ? "Ovozni o'chirish" : 'Ovozni yoqish'}
						onClick={() => setSoundEnabled(p => {
							const next = !p
							// test + brauzer ovozini ochadi (user gesture kerak, aks holda avtomat
							// ijro bloklanadi). Shu payt bo'laklar ham fonda yuklab qo'yiladi.
							if (next) { warmClips(); announce(['ovoz-yoqildi'], 'Ovoz yoqildi') }
							return next
						})}>
						<i className={soundEnabled ? 'uil-volume' : 'uil-volume-mute'} />
					</button>
					{/* SINOV tugmasi — haqiqiy chek yozmasdan e'lonni eshitish uchun.
					    Faqat local ishlab chiqishda ko'rinadi: `npm run build` da NODE_ENV
					    'production' bo'ladi va bu blok bundle'ga umuman tushmaydi, ya'ni
					    keyin qo'lda olib tashlash kerak emas. */}
					{process.env.NODE_ENV !== 'production' && (
						<button className="pb-ico pb-test" title="Sinov: yangi zakaz e'loni"
							onClick={() => {
								// Raqam «Yangi zakaz» ustunidagi HAQIQIY chekdan olinadi —
								// e'lon aynan haqiqiy holatdagidek chiqsin.
								const fresh = activeOrders.filter(o => kbStageOf(o, kbDeptTypes, isWarehouse) === 'new')
								const pool = fresh.length ? fresh : activeOrders
								if (!pool.length) return toast.warning('Sinov uchun zakaz yo\'q')
								const o = pool[Math.floor(Math.random() * pool.length)]
								warmClips() // bosish — user gesture, ovoz shu yerda ochiladi
								if (o.parent_id) {
									announce(['qoshimcha-zakaz', 'chek-raqami', ...numberToClips(o.parent_id),
										(o.cheque_suffix || '').toLowerCase()],
										`Qo'shimcha zakaz! Chek raqami ${numberToUzbekWords(o.parent_id)} ${o.cheque_suffix}`)
								} else {
									announce(['yangi-zakaz', 'chek-raqami', ...numberToClips(o.id)],
										`Yangi zakaz! Chek raqami ${numberToUzbekWords(o.id)}`)
								}
								toast.info(`Sinov #${chequeNo(o)} — «${numberToUzbekWords(o.parent_id || o.id)}»`
									+ (fresh.length ? '' : ' (yangi zakaz yo\'q, boshqasidan olindi)'))
							}}>
							<i className="uil-bell" />
						</button>
					)}
					<button className="pb-ico" onClick={fetchData}><i className="uil-refresh" /></button>
					<button className="pb-ico" onClick={toggleFullscreen}>
						<i className={isFullscreen ? 'uil-compress-arrows' : 'uil-expand-arrows-alt'} />
					</button>
				</div>
			</div>

			{/* Aloqa uzilgani haqida ogohlantirish */}
			{isOffline && (
				<div className="pb-offline">
					<i className="uil-exclamation-triangle" />
					<span>{t('connection_lost')}</span>
					{lastUpdated && <b>{t('last_update')}: {formatDate(lastUpdated, 'HH:mm:ss')}</b>}
				</div>
			)}

			{/* ══ SEX/OMBOR: 4 ustunli kanban (monoblok) ══ */}
			{isKanban && (() => {
				const buckets = { new: [], progress: [], ready: [] }
				activeOrders.forEach(o => {
					const s = kbStageOf(o, kbDeptTypes, isWarehouse)
					if (s && buckets[s]) buckets[s].push(o)
				})
				// Chiqib ketganlarni ham BO'LIM bo'yicha filtrlaymiz: profnastil ustasi
				// aksessuarning chiqqan zakazini ko'rmasligi kerak. Ombor (kbDeptTypes
				// null) hammasini ko'radi — u baribir hammasini chiqaradi.
				// Eski chekda `shipped_types` bo'lmasligi mumkin — ularni yashirmaymiz.
				const shipped = kbDeptTypes
					? completedOrders.filter(o => !o.shipped_types || !o.shipped_types.length
						|| o.shipped_types.some(t => kbDeptTypes.includes(Number(t))))
					: completedOrders
				const byStage = {
					new: sortOrders(buckets.new),
					progress: sortOrders(buckets.progress),
					// Omborda kutish bo'yicha, sexlarda vada bo'yicha
					ready: isWarehouse ? sortForWarehouse(buckets.ready) : sortOrders(buckets.ready),
					shipped,
				}
				const solo = !isWarehouse && visibleDepts.length === 1 ? visibleDepts[0] : null
				const soloOrders = solo
					? activeOrders.filter(o => (o.departments || []).some(d => Number(d.product_type) === solo.type && !d.dept_ready))
					: []
				return (
					<>
						{/* Bo'lim sarlavhasi — ustunlar ustida bir marta (ilgari har ustunda edi) */}
						{/* Tepada FAQAT bo'lim nomi va kunlik yuklanish. Chek soni va metr
						    ustunlarga ko'chirildi: ilgari tepadagi «3 chek · 1300 m» Tayyor
						    ustunini sanamas edi va ekranda 5 ta karta turganda 3 deb ko'rsatib
						    chalg'itardi (eski bir ustunli ko'rinishdan qolgan hisob). */}
						{/* OMBOR jamlanmasi: ustunlari olib tashlangani uchun ishlab
						    chiqarishdagi zakazlar ko'rinmay qolgan edi — soni shu yerda
						    turadi, kartochkalari esa ekranni band qilmaydi. */}
						{isWarehouse && (
							<div className="kb-head is-ombor" style={{ '--accent': '#0284c7' }}>
								<span className="kb-head-name"><i className="uil-box me-1" />Ombor</span>
								<span className="kb-sum is-ready">
									<b>{byStage.ready.length}</b> chek tayyor
								</span>
								<span className="kb-sum">
									<b>{buckets.new.length + buckets.progress.length}</b> ishlab chiqarishda
								</span>
								<span className="kb-sum is-mut">
									<b>{byStage.shipped.length}</b> chiqib ketgan
								</span>
							</div>
						)}
						{solo && (
							<div className="kb-head" style={{ '--accent': solo.accent }}>
								<span className="kb-head-name">{solo.label}</span>
								<LoadBar queue={deptQueueMetr(soloOrders, solo.type)} capacity={targets[solo.type]?.target}
									unit={solo.type === 5 ? 'kv.m' : 'm'} />
							</div>
						)}
						<div className="kb">
							{/* OMBOR: «Yangi zakaz» va «Jarayonda» ustunlari ko'rsatilmaydi (user
							    qarori). Omborchi ular bilan hech nima qila olmaydi — chiqarib
							    yuborish faqat TAYYOR da. 34 ta amalsiz kartochka ekranning
							    yarmini egallab, asosiy ustunni siqib turardi. */}
							{/* OMBORCHI TO'LIQ SIKLNI KO'RADI (2026-08-26, egasining talabi):
							    Yangi va Jarayonda — BARCHA bo'limlarning cheklari (kbDeptTypes
							    null bo'lgani uchun o'zi shunday), Tayyor — faqat haqiqatan
							    tayyorlar. Ilgari omborchiga faqat Tayyor+Chiqib ko'rsatilardi. */}
							{KB_STAGES.map(st => {
								const list = byStage[st.key] || []
								// Chek chiqarish: sexlarda 1- va 4-ustunda; omborda TAYYOR da ham
								// kerak — chiqarib yuborayotganda chek bilan beriladi (user talabi).
								const showPrint = st.key === 'new' || st.key === 'shipped'
									|| (isWarehouse && st.key === 'ready')
								// Sarlavha jamlanmasi: hajm (bitta bo'limli ustada) + navbatdagi ish vaqti
								const colAmount = kbColAmount(list, kbDeptTypes)
								const colMinutes = kbColMinutes(list, kbDeptTypes, st.key)
								const colHeadText = [colAmount, colMinutes].filter(Boolean).join(' · ')
								return (
									/* Omborda ikki ustun TENG (50/50) — 75/25 da TAYYOR ustuni
									   haddan tashqari kengayib, ko'z adashardi. Kartochkalar
									   baribir to'r bo'lib teriladi, ya'ni joy behuda ketmaydi. */
									<div key={st.key} className="kb-col" style={{ '--accent': st.accent }}>
										<div className="kb-col-head">
											<span>{st.label}</span>
											{/* Hajm va ish vaqti BITTA elementda: `.kb-col-head u` da
											    `margin-left:auto` bor — ikkinchi <u> qo'shilsa u ham
											    o'ziga joy talab qilib, sarlavha ikkiga yorilardi. */}
											{colHeadText && <u>{colHeadText}</u>}
											<b>{list.length}</b>
										</div>
										<div className="kb-col-body">
											{list.length === 0 ? (
												<div className="kb-empty"><i className="uil-check-circle" />Zakaz yo'q</div>
											) : (() => {
												/* OMBORCHI — ixcham qator (jarayon o'sha-o'sha: hudud
												   guruhlash, kutish, chiqarish; faqat zichroq). Sex —
												   avvalgidek KbCard. */
												const card = (order, showRoll) => isWarehouse ? (
													<KbRow key={order.id} order={order} stage={st.key}
														onShip={st.key === 'ready' ? openShipModal : null}
														/* Haydovchi FAQAT Tayyor/Chiqib ketganda so'raladi
														   (egasining talabi) — yangi/jarayonda to'g'ridan-to'g'ri */
														/* Chop etish DOIM to'g'ridan-to'g'ri — haydovchi FAQAT
														   «Chiqarib yuborish»da so'raladi (egasining qarori) */
														onPrint={printShipDocDirect}
														onTiming={openTiming}
														onStall={openStall}
														onPhotos={openPhotos} />
												) : (
													<KbCard key={order.id} order={order} stage={st.key} deptTypes={kbDeptTypes}
														onAdvance={(st.key === 'new' || st.key === 'progress') && canAdvance ? advanceStage : null}
														onShip={st.key === 'ready' && isWarehouse ? openShipModal : null}
														onPrint={printCheque}
														onTiming={openTiming}
														showPrint={showPrint} showRoll={showRoll} hideWork={isWarehouse}
														onPause={isWarehouse ? null : (o => setPauseModal({ show: true, order: o, reason: '' }))}
														onResume={isWarehouse ? null : doResume}
																						/* Sotuvchi to'xtatgan bo'lsa usta ocholmaydi */
														onTimeline={openTimeline}
														onLateReason={isWarehouse ? null : openLate} />
												)
												// OMBOR, TAYYOR ustuni — HUDUD (yo'nalish) bo'yicha guruhlash
												if (isWarehouse && st.key === 'ready') {
													return groupByRegion(list).map(g => (
														<React.Fragment key={g.key}>
															<div className="kb-group is-region">
																<span className="kb-group-name">
																	<i className="uil-map-marker me-1" />
																	{g.name || 'Hudud belgilanmagan'}
																	{g.parent && <em> · {g.parent}</em>}
																</span>
																<span className="kb-group-n">{g.orders.length}</span>
															</div>
															{g.orders.map(o => card(o, false))}
														</React.Fragment>
													))
												}
												// Guruhlash FAQAT sex bo'limlarida (Profnastil/Aksessuar/Tunukafon)
												// va faqat REJALASHTIRILADIGAN ustunlarda: tayyor va chiqib
												// ketganda o'rnatishni rejalashtirishning ma'nosi yo'q.
												if (!rollGroupType || !(st.key === 'new' || st.key === 'progress')) {
													return list.map(o => card(o, true))
												}
												return groupByRoll(list, rollGroupType).map(g => (
													g.orders.length < 2
														? g.orders.map(o => card(o, true))   // yolg'iz — rulon kartochkada
														: <React.Fragment key={g.key}>
															<div className="kb-group">
																<span className="kb-group-name">
																	<i className="uil-layer-group me-1" />{g.name || 'Rulonsiz'}
																</span>
																{g.mixed && <span className="kb-group-mix" title="Bu chekda bir nechta g'altak bor">aralash</span>}
																<span className="kb-group-m">{trimNum(g.metr, 1)} {rollGroupType === 5 ? 'kv.m' : 'metr'}</span>
																<span className="kb-group-n">{g.orders.length}</span>
															</div>
															{g.orders.map(o => card(o, false))}
														</React.Fragment>
												))
											})()}
										</div>
									</div>
								)
							})}
						</div>
					</>
				)
			})()}

			{/* ══ IXCHAM: sotuvchi / ega / direktor ══ */}
			{useCompact &&
				<BoardCompact orders={activeOrders} completed={completedOrders}
					depts={visibleDepts} onTimeline={openTimeline} onPhotos={openPhotos} />}

			{/* Ustunlar — kartochka ko'rinishi (o'zgarmagan) */}
			{!isKanban && !useCompact && <>
			<div className="pb-cols">
				{userRole !== 'ROLE_WAREHOUSE' && visibleDepts.map(dept => {
					const { tirik, eski } = getDeptOrders(dept.type)
					const ochiq = showAbandoned[dept.type]
					const ro_yxat = ochiq ? [...tirik, ...eski] : tirik
					return (
						<div key={dept.type} className="pb-col" style={{ '--accent': dept.accent }}>
							<div className="pb-col-head">
								<span className="pb-col-name">{dept.label}</span>
								<span className="pb-col-count">{tirik.length}</span>
							</div>
							{/* Statistika ham, yuklanish ham FAQAT tirik ish bo'yicha —
							    aks holda o'lik buyurtmalar foizni shishirib, yolg'on
							    ogohlantirish beradi (411% o'rniga haqiqiy 111%). */}
							<StatsBar orders={tirik} deptType={dept.type} />
							<LoadBar queue={deptQueueMetr(tirik, dept.type)} capacity={targets[dept.type]?.target} unit={dept.type === 5 ? 'kv.m' : 'm'} />

							{eski.length > 0 &&
								<button className="pb-abandoned-toggle"
									onClick={() => setShowAbandoned(p => ({ ...p, [dept.type]: !p[dept.type] }))}>
									<i className={ochiq ? 'uil-eye-slash' : 'uil-archive'} />
									{ochiq ? 'Tashlandiqni yashirish' : `${eski.length} ta tashlandiq (${ABANDONED_DAYS}+ kun)`}
								</button>}

							<div className="pb-col-body">
								{ro_yxat.length === 0 ? (
									<div className="pb-empty"><i className="uil-check-circle" /><span>Zakaz yo'q</span></div>
								) : (
									ro_yxat.map((order, idx) => (
										<OrderCard key={order.id} order={order} deptType={dept.type} index={idx + 1}
											onPrint={printCheque}
											onAdvance={canAdvance ? advanceStage : null} managerView={isManagerView}
											onTimeline={openTimeline} />
									))
								)}
							</div>
						</div>
					)
				})}

				{/* Ombor */}
				{userRole !== 'ROLE_WORKSHOP' && (
				<div className="pb-col" style={{ '--accent': OMBOR_ACCENT }}>
					<div className="pb-col-head">
						<span className="pb-col-name"><i className="uil-box" /> Ombor</span>
						<span className="pb-col-count">{warehouseOrders.length}</span>
					</div>
					<StatsBar orders={warehouseOrders} deptType={null} />
					<div className="pb-col-body">
						{warehouseOrders.length === 0 ? (
							<div className="pb-empty"><i className="uil-box" /><span>Tayyor zakaz yo'q</span></div>
						) : (
							warehouseOrders.map(order => (
								<OrderCard key={order.id} order={order} deptType={null} isOmbor onShip={openShipModal}
									onPrint={printCheque} onTimeline={openTimeline} managerView={isManagerView} />
							))
						)}
					</div>
				</div>
				)}
			</div>

			{/* Chiqib ketganlar */}
			<div className="pb-shipped">
				<div className="pb-shipped-head">
					<i className="uil-truck" /> Chiqib ketganlar — so'nggi 3 kun
					<span className="pb-count">{completedOrders.length}</span>
				</div>
				{completedOrders.length === 0 ? (
					<div className="pb-shipped-empty">So'nggi 3 kunda bajarilgan zakaz yo'q</div>
				) : (
					<div className="pb-shipped-scroll">
						<table className="pb-tbl">
							<thead>
								<tr>
									<th style={{ width: 90 }}>Chek #</th>
									<th>{t('client')}</th>
									<th style={{ width: 150 }}>{t('cashier')}</th>
									<th style={{ width: 160 }}>{t('deadline')}</th>
									<th style={{ width: 180 }}>Chiqqan bo'limlar</th>
									<th style={{ width: 170 }}>Chiqib ketdi</th>
								</tr>
							</thead>
							<tbody>
								{completedOrders.map(order => (
									<tr key={order.id}>
										<td className="pb-tbl-no">#{order.id}</td>
										<td className="pb-tbl-ell">{order.client_name}</td>
										<td>{order.cashier_name}</td>
										<td>{order.delivery_date ? formatDate(order.delivery_date, 'dd.MM.yyyy HH:mm') : <span className="pb-tbl-mut">—</span>}</td>
										<td>{order.fully_closed ? <span className="badge bg-success">To'liq</span> : <span className="badge bg-warning text-dark">Qisman: {order.shipped_depts || '—'}</span>}</td>
										<td className="pb-tbl-done">{order.shipped_at ? formatDate(order.shipped_at, 'dd.MM.yyyy HH:mm') : '—'}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
			</>}
		</div>

		{/* O'zgarishlar tarixi (audit log) */}
		{/* Qisman chiqarish oynasi */}
		<Modal show={shipModal.show} animation={false} centered onHide={() => setShipModal({ show: false, order: null, selected: [] })}>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: 17 }}><i className="uil-truck me-1" />Chiqarib yuborish {shipModal.order ? `— #${chequeNo(shipModal.order)}` : ''}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="text-muted mb-2" style={{ fontSize: 13 }}>{shipModal.order?.client_name}</div>
				{(shipModal.order?.departments || []).length === 0 && (
					<div className="alert alert-info mb-2" style={{ fontSize: 13 }}>
						<i className="uil-box me-1" />
						Bu chek ishlab chiqarishdan o'tmaydi ({shipModal.order?.all_type_names || 'ombordan'}) —
						to'liq chiqariladi.
					</div>
				)}
				<div className="d-flex flex-column gap-2">
					{(shipModal.order?.departments || []).map((d, i) => {
						const type = Number(d.product_type)
						const checked = shipModal.selected.includes(type)
						return (
							<div key={i} className={"d-flex align-items-center justify-content-between p-2 rounded border " + (d.dept_ready ? "border-success" : "border-warning")}
								style={{ opacity: d.dept_ready ? 1 : 0.55, cursor: d.dept_ready ? "pointer" : "not-allowed" }}
								onClick={() => d.dept_ready && toggleShipDept(type)}>
								<div className="d-flex align-items-center gap-2">
									<input type="checkbox" className="form-check-input mt-0" checked={checked} disabled={!d.dept_ready} readOnly />
									<span className="fw-bold">{d.type_name}</span>
									<span className={"badge " + (d.dept_ready ? "bg-success" : "bg-warning text-dark")}>{d.dept_ready ? "Tayyor" : "Jarayonda"}</span>
								</div>
								<span className="text-muted" style={{ fontSize: 12 }}>{d.dept_ready ? "chiqadi" : "tayyor emas"}</span>
							</div>
						)
					})}
				</div>
				<div className="text-muted mt-2 mb-3" style={{ fontSize: 12 }}>Tanlangan bo'limlar chiqadi, qolganlar ishlab chiqarishda qoladi. Hammasi chiqsa — chek yopiladi.</div>

				{/* ═══ HAYDOVCHI — MAJBURIY (2026-08-26): tovar kim bilan ketyapti
				    yozilmasdan chiqarib bo'lmaydi; istisno — «Mijoz o'zi olib
				    ketadi». Tasdiqlagach yuk xati O'ZI chop etiladi. */}
				<div className="border-top pt-3">
					<div className="fw-bold mb-2" style={{ fontSize: 13.5 }}>
						<i className="uil-truck me-1" />Kim olib ketadi?
					</div>
					<DriverPicker st={shipModal} showSelfPickup
						set={p => setShipModal(prev => ({ ...prev, ...p }))} />
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button className="btn btn-secondary" onClick={() => setShipModal({ show: false, order: null, selected: [] })}>Bekor</button>
				{(() => {
					const noDepts = (shipModal.order?.departments || []).length === 0
					return (
						<button className="btn btn-success"
							disabled={!noDepts && shipModal.selected.length === 0} onClick={confirmShip}>
							<i className="uil-truck me-1" />
							{noDepts ? 'Chiqarish' : `Chiqarish (${shipModal.selected.length})`}
						</button>
					)
				})()}
			</Modal.Footer>
		</Modal>

		{/* SEXDA TO'XTATISH — sabab bilan. Sabab MAJBURIY emas, lekin
		    tavsiya etiladi: rahbar «nega to'xtadi» degan savolga aynan shu
		    yozuvlardan javob topadi. */}
		<Modal show={pauseModal.show} animation={false} centered
			onHide={() => setPauseModal({ show: false, order: null, reason: '' })}>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: 17 }}>
					<i className="uil-pause-circle me-1" />Ishni to'xtatish
					{pauseModal.order && <span className="text-muted ms-2" style={{ fontSize: 13 }}>#{chequeNo(pauseModal.order)}</span>}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="mb-2" style={{ fontSize: 13, color: '#64748b' }}>
					To'xtatilgan buyurtma kengashda yig'ilib qoladi va bosqich tugmasi
					berilmaydi. Ochishni ham SIZ qilasiz.
				</div>
				<label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Sababi</label>
				<div className="d-flex flex-wrap gap-2 mb-2">
					{['Material yetmadi', 'Stanok buzildi', 'Navbat ko\'p', 'Mijoz o\'zgartirdi'].map(r => (
						<button key={r} type="button" className="btn btn-sm btn-outline-secondary"
							onClick={() => setPauseModal(p => ({ ...p, reason: r }))}>{r}</button>
					))}
				</div>
				<textarea className="form-control" rows="2" placeholder="yoki o'zingiz yozing"
					value={pauseModal.reason}
					onChange={e => setPauseModal(p => ({ ...p, reason: e.target.value }))} />
			</Modal.Body>
			<Modal.Footer>
				<button className="btn btn-warning"
					onClick={() => setPauseModal({ show: false, order: null, reason: '' })}>Bekor</button>
				<button className="btn btn-primary" onClick={doPause}>To'xtatish</button>
			</Modal.Footer>
		</Modal>

		{/* KECHIKISH SABABI — buyurtma vadadan o'tib ketganda so'raladi.
		    Ro'yxat qat'iy: oy oxirida «kechikishlarning nechasi nimadan»
		    degan savolga javob aynan shu yig'ilgan sabablardan chiqadi. */}
		<Modal show={lateModal.show} animation={false} centered
			onHide={() => setLateModal({ show: false, order: null, reason: '', note: '' })}>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: 17 }}>Kechikish sababi
					{lateModal.order && <span className="text-muted ms-2" style={{ fontSize: 13 }}>#{chequeNo(lateModal.order)}</span>}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="d-flex flex-column gap-2">
					{lateReasons.map(r => (
						<label key={r.id} className="d-flex align-items-center gap-2 cursor">
							<input type="radio" name="lr" checked={lateModal.reason === r.id}
								onChange={() => setLateModal(p => ({ ...p, reason: r.id }))} />
							<span>{r.name}</span>
						</label>
					))}
				</div>
				<label className="form-label mt-3">Izoh (ixtiyoriy)</label>
				<input className="form-control" value={lateModal.note}
					onChange={e => setLateModal(p => ({ ...p, note: e.target.value }))} />
			</Modal.Body>
			<Modal.Footer>
				<div className="d-flex w-100 gap-2">
					<button className="btn btn-warning w-100"
						onClick={() => setLateModal({ show: false, order: null, reason: '', note: '' })}>Bekor</button>
					<button className="btn btn-primary w-100" disabled={!lateModal.reason} onClick={saveLate}>Saqlash</button>
				</div>
			</Modal.Footer>
		</Modal>

		{/* YUK RASMLARI (2026-09-01) — omborchi Telegramga tashlagan rasm.
		    Rasm base64 bo'lib keladi: ochiq URL yo'q, chunki yukda mijoz,
		    haydovchi va mashina raqami ko'rinadi. */}
		<Modal show={photoModal.show} animation={false} centered size="lg"
			onHide={() => setPhotoModal({ show: false, order: null, list: [], loading: false })}>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: 17 }}>
					{t('shipment_photos')}
					{photoModal.order && <span className="text-muted ms-2" style={{ fontSize: 13 }}>#{chequeNo(photoModal.order)}</span>}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{photoModal.loading
					? <div className="text-center py-4 text-muted">...</div>
					: photoModal.list.length === 0
						? <div className="text-center py-4 text-muted">
							<i className="uil-camera-slash d-block" style={{ fontSize: 34, opacity: .4 }} />
							{t('shipment_no_photos')}
						</div>
						: <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 10 }}>
							{photoModal.list.map(p => (
								<div key={p.id} style={{ border: '1px solid var(--dk-border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
									{p.thumb
										? <img src={p.thumb} alt="" style={{ display: 'block', width: '100%' }} />
										: <div className="text-center py-4 text-muted" style={{ fontSize: 13 }}>—</div>}
									<div style={{ fontSize: 12, padding: '4px 7px' }}>
										{p.author && <b>{p.author}</b>}
										{p.created_at && <span className="text-muted"> · {formatDate(p.created_at, 'dd.MM HH:mm')}</span>}
										{p.caption && <div className="text-muted">{p.caption}</div>}
									</div>
								</div>
							))}
						</div>}
			</Modal.Body>
		</Modal>

		{/* OMBORDA TURIB QOLISH SABABI (2026-08-26) — lateModal naqshi */}
		<Modal show={stallModal.show} animation={false} centered
			onHide={() => setStallModal({ show: false, order: null, reason: '', note: '' })}>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: 17 }}>
					Nega turib qolgan?
					{stallModal.order && <span className="text-muted ms-2" style={{ fontSize: 13 }}>#{chequeNo(stallModal.order)}</span>}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="d-flex flex-column gap-2">
					{stallReasons.map(r => (
						<label key={r.id} className="d-flex align-items-center gap-2 cursor">
							<input type="radio" name="sr" checked={stallModal.reason === r.id}
								onChange={() => setStallModal(p => ({ ...p, reason: r.id }))} />
							<span>{r.name}</span>
						</label>
					))}
				</div>
				<label className="form-label mt-3">Izoh (ixtiyoriy)</label>
				<input className="form-control" value={stallModal.note}
					onChange={e => setStallModal(p => ({ ...p, note: e.target.value }))} />
			</Modal.Body>
			<Modal.Footer>
				<div className="d-flex w-100 gap-2">
					{/* Buyurtma harakatga keldi — sabab olib tashlanadi */}
					{stallModal.order?.stall_reason &&
						<button className="btn btn-outline-danger"
							onClick={() => saveStall(true)}>Olib tashlash</button>}
					<button className="btn btn-warning w-100"
						onClick={() => setStallModal({ show: false, order: null, reason: '', note: '' })}>Bekor</button>
					<button className="btn btn-primary w-100" disabled={!stallModal.reason}
						onClick={() => saveStall(false)}>Saqlash</button>
				</div>
			</Modal.Footer>
		</Modal>


		{/* CHEK YO'LI — vaqt qayerda ketgani bir qarashda ko'rinadi */}
		<Modal show={tlModal.show} animation={false} centered size="lg"
			onHide={() => setTlModal({ show: false, order: null, data: null, loading: false })}>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: 17 }}>
					<i className="uil-clock-three me-1" />{t('tl_path')}
					{tlModal.order && <span className="text-muted ms-2" style={{ fontSize: 13 }}>#{chequeNo(tlModal.order)} · {tlModal.order.client_name}</span>}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="pb-tl-body">
				{tlModal.loading && <div className="text-center text-muted py-4">Yuklanmoqda...</div>}
				{!tlModal.loading && !tlModal.data && <div className="text-center text-muted py-4">Ma'lumot yo'q</div>}
				{!tlModal.loading && tlModal.data && (<>
					{/* JAMLANMA TEPADA - rahbar avval SHUNI ko'radi.
					    «Qancha vaqt ketdi» va «shundan qanchasi bekor turdi» -
					    qolgan tafsilot faqat shundan keyin kerak bo'ladi. */}
					<div className="pb-tl-cards">
						<div className="pb-tl-card">
							<span className="pb-tl-card-l">{t('tl_total')}</span>
							<b>{shortDur(tlModal.data.jami.umumiy_sec || 0)}</b>
						</div>
						<div className={'pb-tl-card' + (tlModal.data.jami.toxtash_sec > 0 ? ' is-bad' : '')}>
							<span className="pb-tl-card-l">{t('tl_paused')}</span>
							<b>{tlModal.data.jami.toxtash_sec > 0 ? shortDur(tlModal.data.jami.toxtash_sec) : '—'}</b>
						</div>
						{tlModal.data.jami.ochiq_gap_sec != null && (
							<div className="pb-tl-card">
								<span className="pb-tl-card-l">{t('tl_since')}</span>
								<b>{shortDur(tlModal.data.jami.ochiq_gap_sec)}</b>
							</div>
						)}
					</div>

					{/* QADAMLAR. Vaqt qadamlar ORASIDA yoziladi, qadamning o'zida emas:
					    «qayerda vaqt yo'qoldi» degan savolga aynan oraliq javob beradi.
					    Ilgari «+3s» qatorning o'ng chetida turardi va nimani anglatishi
					    tushunarsiz edi. */}
					<div className="pb-tl2">
						{tlModal.data.steps.map((st, i) => {
							const L = historyLabels(t)
							const kind = st.event === 'pause' ? 'is-pause'
								: st.event === 'resume' ? 'is-resume'
									: st.event === 'shipped' ? 'is-done'
										: st.event === 'opened' ? 'is-start' : ''
							return (
								<Fragment key={i}>
									{/* Oraliq - shu qadamgacha qancha kutilgani */}
									{st.gap_sec != null && st.gap_sec > 0 && (
										<div className="pb-tl2-gap"><span>{shortDur(st.gap_sec)}</span></div>
									)}
									<div className={'pb-tl2-step ' + kind}>
										<div className="pb-tl2-dot" />
										<div className="pb-tl2-body">
											<div className="pb-tl2-head">
												<span className="pb-tl2-title">
													{L.events[st.event] || st.title}
													{st.dept && <span className="pb-tl2-dept">{st.dept}</span>}
												</span>
												<span className="pb-tl2-at">{formatDate(st.at, 'dd.MM HH:mm')}</span>
											</div>
											{/* ESKI -> YANGI. Ilgari buni alohida «o'zgarishlar tarixi»
											    oynasi ko'rsatardi; ikkalasi bir xil audit_log dan
											    o'qir edi, shuning uchun birlashtirildi. */}
											{st.old_value != null && st.old_value !== '' ? (
												<div className="pb-tl2-detail">
													<span className="pb-tl2-old">{renderVal(st.event, st.old_value, t, L)}</span>
													<i className="uil-arrow-right" />
													<b>{renderVal(st.event, st.new_value, t, L)}</b>
												</div>
											) : st.detail ? (
												<div className="pb-tl2-detail">{st.detail}</div>
											) : null}
											{st.user && <div className="pb-tl2-user">{st.user}</div>}
										</div>
									</div>
								</Fragment>
							)
						})}
						{/* Hali tugamagan bo'lsa - oxirida ochiq uch */}
						{tlModal.data.jami.ochiq_gap_sec != null && (
							<div className="pb-tl2-step is-open">
								<div className="pb-tl2-dot" />
								<div className="pb-tl2-body">
									<div className="pb-tl2-head">
										<span className="pb-tl2-title">{t('tl_here_now')}</span>
										<span className="pb-tl2-at">{shortDur(tlModal.data.jami.ochiq_gap_sec)}</span>
									</div>
								</div>
							</div>
						)}
					</div>
				</>)}
			</Modal.Body>
		</Modal>

		<Modal show={timing.show} animation={false} centered
			onHide={() => setTiming({ ...timing, show: false })}>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: 17 }}>
					<i className="uil-clock me-1" />#{timing.no}
					{timing.client && <span className="text-muted ms-2" style={{ fontSize: 13 }}>{timing.client}</span>}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{!timing.data ? null : !timing.data.is_exact ? (
					<div className="alert alert-warning mb-0" style={{ fontSize: 13 }}>
						<i className="uil-exclamation-triangle me-1" />
						Bu chekda bir nechta bo'lim bor va bosqich tarixi bo'lim bo'yicha
						yozilmagan (eski yozuv). Shu sababli vaqtni aniq ko'rsatib bo'lmaydi —
						noto'g'ri raqam chiqarmaslik uchun bo'sh qoldirildi.
						Bundan keyingi zakazlarda aniq ko'rinadi.
					</div>
				) : timing.data.stages.length === 0 ? (
					<div className="text-muted text-center py-3" style={{ fontSize: 13 }}>
						Bu chek bo'yicha bosqich o'zgarishi qayd etilmagan
					</div>
				) : (
					<>
						{/* Joriy holat — kartochkadagi yorliq bilan bir xil, oynada ham ko'rinsin */}
						{(() => {
							const last = timing.data.stages[timing.data.stages.length - 1]
							if (!last) return null
							return (
								<div className="d-flex justify-content-between align-items-center mb-3 p-2 rounded"
									style={{ background: '#f6f8fb', border: '1px solid #e5e9f0' }}>
									<span className="text-muted" style={{ fontSize: 12.5 }}>Hozirgi holat</span>
									<span style={{ fontSize: 13.5, fontWeight: 800, color: stageAccent(last.name) }}>
										{last.name}
										{last.done && <span className="text-muted ms-2" style={{ fontWeight: 500, fontSize: 12 }}>
											· chiqib ketgan
										</span>}
									</span>
								</div>
							)
						})()}

						<div className="d-flex justify-content-between align-items-baseline mb-3">
							<span className="text-muted" style={{ fontSize: 13 }}>Jami vaqt</span>
							<span style={{ fontSize: 22, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
								{humanDur(timing.data.total)}
							</span>
						</div>

						<div className="d-flex flex-column gap-2">
							{timing.data.stages.map((s, i) => {
								const pct = timing.data.total > 0 ? Math.round(s.seconds / timing.data.total * 100) : 0
								const color = stageAccent(s.name)
								return (
									<div key={i}>
										<div className="d-flex justify-content-between align-items-baseline">
											<span style={{ fontSize: 13.5, fontWeight: 700, color }}>
												{s.name}
												{!s.done && <span className="text-muted ms-2" style={{ fontWeight: 500, fontSize: 12 }}>
													davom etmoqda
												</span>}
											</span>
											<span style={{ fontSize: 14, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
												{humanDur(s.seconds)}
												<span className="text-muted ms-2" style={{ fontWeight: 500, fontSize: 12 }}>{pct}%</span>
											</span>
										</div>
										<div style={{ height: 7, background: '#eef2f7', borderRadius: 4, marginTop: 3, overflow: 'hidden' }}>
											<div style={{ width: pct + '%', height: '100%', background: color, opacity: s.done ? 1 : .55 }} />
										</div>
										<div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
											{formatDate(s.from, 'dd.MM HH:mm')} — {s.to ? formatDate(s.to, 'dd.MM HH:mm') : '…'}
											{s.by && <span className="ms-2">· {s.by}</span>}
										</div>
									</div>
								)
							})}
						</div>
					</>
				)}
			</Modal.Body>
		</Modal>

		<div style={{ display: 'none' }}>
			<Cheque data={printData} printChequeRef={printChequeRef} />
		</div>
		</>
	)

	if (isFullscreen) {
		return (
			/* ⚠️ z-index 1030 — 99999 EMAS. Modal oynalar (bosqich vaqtlari, tarix,
			   chiqarish) portal orqali `document.body` ga chiziladi va Bootstrap'da
			   ularning qatlami 1050/1055. Bu o'ram 99999 bo'lganda to'liq ekran
			   rejimida oynalar ochilardi-yu, kengash ostida ko'rinmay qolardi.
			   1030 — modal qatlamidan past, qolgan hamma narsadan baland. */
			<div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1030 }}>
				{boardContent}
			</div>
		)
	}

	return boardContent
}

const PB_CSS = `
.pb{--bg:#f1f4f9;--panel:#ffffff;--card:#ffffff;--card2:#f6f8fb;--bd:#e3e8f0;--bd2:#cfd6e2;--tx:#1e293b;--mut:#64748b;--fnt:#94a3b8;--mono:ui-monospace,"SF Mono","Cascadia Code",Consolas,monospace;
	height:100vh;display:flex;flex-direction:column;background:var(--bg);color:var(--tx);
	font-family:"Segoe UI",system-ui,-apple-system,Roboto,Arial,sans-serif;overflow:hidden}
.pb *{box-sizing:border-box}
.pb .uil-user,.pb .uil-user-circle,.pb .uil-clock,.pb .uil-receipt,.pb .uil-ruler,.pb .uil-box,.pb .uil-print,.pb .uil-truck,.pb .uil-check-circle{font-style:normal}

/* Topbar */
.pb-top{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:9px 16px;background:var(--panel);border-bottom:1px solid var(--bd);flex-shrink:0;flex-wrap:wrap;box-shadow:0 1px 3px rgba(15,23,42,.05)}
.pb-title{display:flex;align-items:center;gap:11px}
.pb-title h5{margin:0;font-size:15px;font-weight:800;letter-spacing:.2px}
.pb-live{display:inline-flex;align-items:center;gap:6px;font-size:10px;font-weight:800;letter-spacing:.12em;color:#059669;background:rgba(5,150,105,.1);padding:4px 9px;border-radius:6px;border:1px solid rgba(5,150,105,.25)}
.pb-live-dot{width:7px;height:7px;border-radius:50%;background:#059669;box-shadow:0 0 0 0 rgba(5,150,105,.7);animation:pbPulse 1.8s infinite}
.pb-count{display:inline-grid;place-items:center;min-width:24px;height:22px;padding:0 7px;background:#2563eb;color:#fff;font-size:12.5px;font-weight:800;border-radius:11px;font-variant-numeric:tabular-nums}
.pb-controls{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.pb-seg{display:flex;background:#eef2f8;border:1px solid var(--bd);border-radius:9px;padding:3px;gap:2px}
.pb-seg button{border:0;background:transparent;color:var(--mut);font:inherit;font-size:12px;font-weight:600;padding:5px 11px;border-radius:6px;cursor:pointer;white-space:nowrap}
.pb-seg button:hover{color:var(--tx)}
.pb-seg button.on{background:#2563eb;color:#fff}
.pb-clock{font-family:var(--mono);font-size:12.5px;color:var(--mut);white-space:nowrap;font-variant-numeric:tabular-nums}
.pb-clock b{margin-left:5px;color:var(--fnt);font-weight:700}
.pb-clock b.urgent{color:#e11d48}
.pb-ico{width:34px;height:34px;display:grid;place-items:center;border:1px solid var(--bd);background:var(--panel);color:var(--mut);border-radius:8px;cursor:pointer;font-size:16px}
.pb-ico:hover{color:var(--tx);border-color:var(--bd2)}
.pb-ico.is-on{color:#059669;border-color:rgba(5,150,105,.4)}
/* Sinov tugmasi — haqiqiy tugmalardan ajralib tursin (faqat local'da chiqadi) */
.pb-test{color:#7c3aed;border-color:rgba(124,58,237,.4);border-style:dashed}
.pb-test:hover{color:#6d28d9;background:rgba(124,58,237,.07)}

/* Columns */
/* ═══ SEX/OMBOR: 4 ustunli kanban (monoblok, ~1024px dan boshlab) ═══
   Kartochkalar ataylab ixcham — monoblokda bitta chek o'rniga bir nechtasi
   ko'rinishi kerak. O'lchamlar clamp() bilan: kichik ekranda siqiladi,
   kattasida cho'ziladi, alohida media-query kerak emas. */
.kb-head{display:flex;align-items:center;gap:clamp(8px,1.2vw,18px);padding:6px clamp(8px,1vw,14px);
	background:var(--panel);border-bottom:2px solid var(--accent);flex-shrink:0;flex-wrap:wrap}
.kb-head-name{font-size:clamp(12px,1.15vw,15px);font-weight:800;letter-spacing:.09em;text-transform:uppercase;color:var(--accent)}
/* Ombor jamlanmasi */
.kb-head.is-ombor{gap:clamp(10px,1.6vw,26px)}
.kb-sum{font-size:clamp(11px,1vw,13.5px);font-weight:600;color:var(--mut);white-space:nowrap}
.kb-sum b{font-family:var(--mono);font-size:clamp(15px,1.4vw,21px);font-weight:800;color:var(--tx);
	margin-right:4px;font-variant-numeric:tabular-nums}
.kb-sum.is-ready b{color:#0284c7}
.kb-sum.is-mut,.kb-sum.is-mut b{color:var(--fnt)}
.kb-head .pb-col-stats{border:0;background:transparent;padding:0;font-size:clamp(11px,1vw,13px)}
.kb-head .pb-target{flex:1;min-width:150px;padding:0}
.kb{display:flex;gap:clamp(5px,.6vw,10px);padding:clamp(5px,.6vw,10px);flex:1;min-height:0;overflow:hidden}
.kb-col{flex:1 1 0;min-width:0;display:flex;flex-direction:column;background:var(--panel);
	border:1px solid var(--bd);border-top:3px solid var(--accent);border-radius:9px;overflow:hidden}
.kb-col-head{display:flex;align-items:center;justify-content:space-between;gap:5px;
	padding:clamp(3px,.4vw,6px) clamp(6px,.6vw,10px);
	background:color-mix(in srgb,var(--accent) 12%,transparent);border-bottom:1px solid var(--bd)}
.kb-col-head span{font-size:clamp(10px,.92vw,13px);font-weight:800;letter-spacing:.05em;text-transform:uppercase;
	color:var(--accent);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.kb-col-head u{margin-left:auto;text-decoration:none;font-family:var(--mono);flex-shrink:0;
	font-size:clamp(10px,.92vw,13px);font-weight:800;color:var(--accent);opacity:.9;
	font-variant-numeric:tabular-nums;white-space:nowrap}
.kb-col-head b{display:inline-grid;place-items:center;min-width:20px;height:20px;padding:0 6px;flex-shrink:0;
	background:var(--accent);color:#fff;font-size:11.5px;font-weight:800;border-radius:10px;font-variant-numeric:tabular-nums}
.kb-col-body{flex:1;overflow-y:auto;padding:clamp(3px,.35vw,6px);display:flex;flex-direction:column;gap:clamp(3px,.35vw,6px)}
.kb-col-body::-webkit-scrollbar{width:6px}
.kb-col-body::-webkit-scrollbar-thumb{background:var(--bd2);border-radius:3px}
.kb-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;
	color:var(--fnt);font-size:11.5px;padding:18px 0}
.kb-empty i{font-size:24px;opacity:.3}
.kb-card.is-clickable{cursor:pointer}
.kb-card.is-clickable:hover{border-color:var(--fnt)}
.kb-card{background:var(--card);border:1px solid var(--bd);border-left:3px solid var(--stripe);
	border-radius:6px;padding:clamp(4px,.4vw,6px) clamp(5px,.5vw,8px)}
.kb-card-top{display:flex;align-items:baseline;gap:5px;min-width:0}
.kb-no{font-family:var(--mono);font-size:clamp(15px,1.35vw,21px);font-weight:800;line-height:1.1;
	color:#0f172a;font-variant-numeric:tabular-nums}
.kb-no i{font-style:normal;color:var(--fnt);font-size:.72em}
.kb-tag{font-size:9px;font-weight:800;letter-spacing:.04em;color:#fff;padding:2px 6px;border-radius:9px;
	white-space:nowrap;flex-shrink:0}
.kb-tag.is-done{background:#059669}
/* ZARUR zakaz — ustun tepasida, uzoqdan ko'rinsin. Puls sekin: sexdagi ekran
   kun bo'yi yonib turadi, tez miltillash charchatadi. */
.kb-card.is-urgent{border-color:#e11d48;border-left-color:#e11d48;
	box-shadow:0 0 0 2px rgba(225,29,72,.16);background:rgba(225,29,72,.035)}
.kb-tag.is-urgent{background:#e11d48;animation:kbUrg 2.2s ease-in-out infinite}
.kb-tag.is-urgent i{margin-right:2px}
@keyframes kbUrg{0%,100%{opacity:1}50%{opacity:.45}}
@media (prefers-reduced-motion:reduce){.kb-tag.is-urgent{animation:none}}
/* Mijoz chek raqami bilan bir qatorda — qolgan joyni egallaydi va qisqaradi */
.kb-client{flex:1;min-width:0;font-size:clamp(9.5px,.8vw,11.5px);font-weight:600;color:var(--mut);
	white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
/* Yolg'iz zakazdagi rulon nomi — mijoz yonida, xiraroq */
.kb-roll{margin-left:6px;color:var(--fnt);font-weight:700}
/* Mijoz raqami — omborchiga qo'ng'iroq qilish uchun, uzoqdan o'qilsin */
.kb-phone{margin-left:8px;font-family:var(--mono);font-size:clamp(11px,.98vw,14px);
	font-weight:800;color:var(--tx);font-variant-numeric:tabular-nums}
.kb-roll i{margin-right:2px;opacity:.7}

/* G'ALTAK GURUHI — bir xil rulondagi zakazlar. Sarlavha ixcham (bir qator),
   chunki u ustundagi zakaz sonini kamaytirmasligi kerak. */
.kb-group{margin:2px 0 1px;display:flex;align-items:center;gap:5px;padding:2px 5px;border-radius:5px;
	background:#eef2f7;border-left:3px solid #64748b}
.kb-group-name{flex:1;min-width:0;font-size:clamp(9.5px,.82vw,11.5px);font-weight:800;color:#334155;
	white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.kb-group-n{flex-shrink:0;font-size:clamp(9px,.75vw,10.5px);font-weight:800;color:#fff;background:#64748b;
	border-radius:9px;padding:1px 6px;font-variant-numeric:tabular-nums}
.kb-group-m{flex-shrink:0;font-family:var(--mono);font-size:clamp(9px,.75vw,10.5px);font-weight:700;
	color:#475569;font-variant-numeric:tabular-nums}
.kb-group-mix{flex-shrink:0;font-size:9px;font-weight:800;color:#b45309}
/* Hudud guruhi — ombor uchun (yetkazish yo'nalishi) */
.kb-group.is-region{background:#e0f2fe;border-left-color:#0284c7}
.kb-group.is-region .kb-group-name{color:#075985}
.kb-group.is-region .kb-group-n{background:#0284c7}
.kb-group-name em{font-style:normal;font-weight:600;opacity:.75}
/* Vada bloki — kartochkadagi eng ko'zga tashlanadigan raqam, MARKAZDA
   (ilgari chekkada qolardi). Ochilgan vaqt (kb-ago) absolyut burchakda:
   margin-left:auto bilan qo'yilsa markazni surib yuborardi.
   DIQQAT: bu blok JS shablon-satri ichida — izohda backtick YOZMANG. */
.kb-when{position:relative;display:grid;grid-template-columns:1fr auto 1fr;align-items:center;
	margin-top:3px;padding:clamp(2px,.25vw,4px) clamp(5px,.55vw,8px);border-radius:6px;
	background:color-mix(in srgb,var(--u) 13%,var(--card2));
	border:1px solid color-mix(in srgb,var(--u) 40%,transparent)}
.kb-when-main{display:flex;align-items:baseline;justify-content:center;gap:6px;min-width:0}
.kb-when i{color:var(--u);align-self:center}
.kb-when-d{font-size:clamp(9px,.78vw,11.5px);font-weight:700;color:var(--u);opacity:.85;white-space:nowrap}
.kb-when-t{font-family:var(--mono);font-size:clamp(15px,1.4vw,22px);font-weight:800;line-height:1.05;
	color:var(--u);font-variant-numeric:tabular-nums;letter-spacing:-.5px}
.kb-when.is-none{background:var(--card2);border:1px dashed var(--bd2);color:var(--fnt);
	font-size:clamp(10px,.9vw,12px);font-weight:600;align-items:center;gap:5px}
.kb-when.is-none i{color:var(--fnt)}
/* Jami vaqt — vada blokining O'NG ustunida (grid: [metr] [vaqt] [jami]).
   Ilgari bu joyda chek ochilgan SOAT turardi; jami vaqt ustaga foydaliroq. */
.kb-total{grid-column:3;justify-self:end;display:inline-flex;align-items:center;gap:2px;
	font-size:clamp(9.5px,.8vw,11.5px);font-weight:700;color:var(--u);opacity:.85;
	font-variant-numeric:tabular-nums;white-space:nowrap}
.kb-total i{font-size:1.05em}
.kb-when.is-none .kb-total{color:var(--mut)}
.kb-chips{display:flex;flex-wrap:wrap;gap:3px;margin-top:4px}
.kb-chip{font-size:9px;font-weight:700;color:#fff;background:#d97706;padding:2px 6px;border-radius:8px}
.kb-chip.is-green{background:#059669}
/* Ishlab chiqarishsiz chek — ombordan beriladi */
.kb-chip.is-stock{background:#0891b2}
.kb-chip b{font-variant-numeric:tabular-nums;opacity:.9}
/* Ish hajmi — kartochkadagi ikkinchi eng muhim raqam (vadadan keyin) */
/* Ish hajmi vada bloki ICHIDA, chap ustunda — vaqt markazda qolishi uchun
   grid uch ustunli: [metr] [vaqt] [bo'sh]. */
.kb-amount{grid-column:1;justify-self:start;font-family:var(--mono);
	font-size:clamp(10px,.85vw,12.5px);font-weight:800;line-height:1;color:var(--u);opacity:.9;
	font-variant-numeric:tabular-nums;letter-spacing:-.3px;white-space:nowrap}
.kb-when-main{grid-column:2}
.kb-when.is-none .kb-amount{color:var(--mut)}
.kb-btn{width:100%;margin-top:4px;border:0;border-radius:5px;padding:clamp(4px,.45vw,7px) 5px;font:inherit;
	font-size:clamp(10px,.85vw,12px);font-weight:800;color:#fff;cursor:pointer;
	display:flex;align-items:center;justify-content:center;gap:5px}
.kb-btn em{font-style:normal;font-weight:600;opacity:.8;font-size:.85em}
.kb-btn.is-start{background:#2563eb}
.kb-btn.is-ready{background:#059669}
.kb-btn.is-ship{background:#059669}
.kb-btn.is-wait{background:#cbd5e1;color:#64748b;cursor:not-allowed}
/* Amal qatori: ikkalasi ham to'ldirilgan rangli tugma, bir xil balandlikda.
   Adashib bosishdan RANG himoya qiladi, o'lcham emas: bosqich tugmasi ko'k/yashil,
   chek chiqarish esa to'q kulrang-ko'k — ular hech qachon bir-biriga o'xshamaydi.
   Kenglik nisbati 2:1 — asosiy amal baribir kattaroq bo'lib qoladi. */
.kb-actions{display:flex;align-items:stretch;gap:4px;margin-top:4px}
.kb-actions .kb-btn{margin-top:0;flex:2}
.kb-print{flex:1;min-width:0;border:0;border-radius:5px;background:#475569;color:#fff;
	padding:clamp(4px,.45vw,7px) 6px;font:inherit;font-size:clamp(10px,.85vw,12px);
	font-weight:800;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:4px}
.kb-print:hover{background:#334155}
.kb-print span{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}

.pb-cols{display:flex;gap:10px;padding:10px;flex:1;min-height:0;overflow:hidden}
.pb-col{flex:1;min-width:0;display:flex;flex-direction:column;background:var(--panel);border:1px solid var(--bd);border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(15,23,42,.05)}
.pb-col-head{display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:linear-gradient(180deg,color-mix(in srgb,var(--accent) 14%,transparent),transparent);border-bottom:1px solid var(--bd);border-top:3px solid var(--accent)}
.pb-col-name{font-size:13px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--accent);display:flex;align-items:center;gap:6px}
.pb-col-count{display:inline-grid;place-items:center;min-width:26px;height:24px;padding:0 8px;background:var(--accent);color:#fff;font-size:13px;font-weight:800;border-radius:12px;font-variant-numeric:tabular-nums}
.pb-col-stats{display:flex;align-items:center;gap:16px;padding:8px 14px;font-size:13px;font-weight:700;color:var(--accent);background:color-mix(in srgb,var(--accent) 6%,transparent);border-bottom:1px solid var(--bd);flex-shrink:0;flex-wrap:wrap;font-variant-numeric:tabular-nums}
.pb-col-stats i{margin-right:5px;opacity:.85}
.pb-col-body{flex:1;overflow-y:auto;padding:9px;display:flex;flex-direction:column;gap:9px}
.pb-col-body::-webkit-scrollbar{width:8px}
.pb-col-body::-webkit-scrollbar-thumb{background:var(--bd2);border-radius:4px}
.pb-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;color:var(--fnt);padding:40px 0}
.pb-empty i{font-size:38px;opacity:.35}
.pb-empty span{font-size:13px}

/* Card */
.pb-card{background:var(--card);border:1px solid var(--bd);border-left:4px solid var(--stripe);border-radius:10px;padding:12px 13px;box-shadow:0 1px 2px rgba(15,23,42,.06);transition:transform .15s,box-shadow .15s}
.pb-card:hover{transform:translateY(-1px);box-shadow:0 6px 18px rgba(15,23,42,.12)}
.pb-card-top{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:9px}
.pb-id-wrap{display:flex;align-items:center;gap:9px;min-width:0}
.pb-index{display:inline-grid;place-items:center;width:26px;height:26px;border-radius:50%;color:#fff;font-size:13px;font-weight:800;flex-shrink:0;font-variant-numeric:tabular-nums}
.pb-cheque{font-family:var(--mono);font-size:23px;font-weight:800;letter-spacing:-.5px;line-height:1;color:#0f172a;font-variant-numeric:tabular-nums;display:flex;align-items:center;gap:4px;flex-wrap:wrap}
.pb-cheque-hash{color:var(--fnt);font-size:17px}
.pb-add{font-family:"Segoe UI",sans-serif;font-size:10px;font-weight:800;letter-spacing:.03em;color:#b45309;background:rgba(245,158,11,.15);border:1px solid rgba(245,158,11,.4);padding:1px 6px;border-radius:5px}
.pb-opened{font-size:11px;color:var(--fnt);margin-top:3px;font-weight:500}
.pb-badge{font-size:11px;font-weight:800;color:#fff;padding:4px 10px;border-radius:20px;white-space:nowrap;flex-shrink:0}
.pb-badge.is-green{background:#059669}.pb-badge.is-amber{background:#d97706}
.pb-depts{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:9px}
.pb-chip{font-size:10.5px;font-weight:700;color:#fff;padding:3px 9px;border-radius:11px}
.pb-chip.is-green{background:#059669}.pb-chip.is-amber{background:#d97706}
.pb-meta{margin-bottom:10px}
.pb-client{font-size:14px;font-weight:650;color:var(--tx);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.pb-client i{color:var(--fnt);margin-right:6px}
.pb-seller{font-size:12px;color:var(--mut);font-weight:500;margin-top:2px}
.pb-seller i{color:var(--fnt);margin-right:6px}
.pb-deadline{border-radius:9px;padding:9px 12px;text-align:center;background:color-mix(in srgb,var(--u) 12%,var(--card2));border:1px solid color-mix(in srgb,var(--u) 40%,transparent)}
.pb-deadline-l{font-size:10px;font-weight:800;letter-spacing:.1em;color:var(--u)}
.pb-deadline-d{font-family:var(--mono);font-size:13px;font-weight:600;line-height:1.1;color:var(--mut);font-variant-numeric:tabular-nums;margin-top:2px}
.pb-deadline-t{font-family:var(--mono);font-size:27px;font-weight:800;color:var(--u);line-height:1.05;margin-top:2px}
.pb-deadline.is-none{background:var(--card2);border:1px dashed var(--bd2);color:var(--fnt);font-size:12.5px;font-weight:600}
.pb-strip{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:7px;padding:6px 11px;border-radius:8px;background:var(--card2);border:1px solid var(--bd)}
.pb-strip-l{font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--mut)}
.pb-strip-l i{margin-right:4px}
.pb-strip-v{font-size:13px;font-weight:800;color:var(--tx);font-variant-numeric:tabular-nums}
.pb-strip-v em{font-style:normal;font-weight:500;color:var(--mut);margin-left:5px}
.pb-strip.is-est .pb-strip-v{color:#2563eb}
.pb-strip.is-est{background:rgba(37,99,235,.08);border-color:rgba(37,99,235,.25)}
.pb-strip.is-progress{background:rgba(217,119,6,.1);border-color:rgba(217,119,6,.3)}
.pb-strip.is-progress .pb-strip-l,.pb-strip.is-progress .pb-strip-v{color:#b45309}
.pb-strip.is-done{background:#059669;border-color:#059669}
.pb-strip.is-done .pb-strip-l{color:rgba(255,255,255,.9)}.pb-strip.is-done .pb-strip-v{color:#fff}
.pb-btn{width:100%;margin-top:9px;border:0;border-radius:8px;padding:9px;font:inherit;font-size:13px;font-weight:800;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px}
.pb-btn.is-ghost{background:transparent;border:1px solid var(--bd2);color:var(--mut);font-weight:600;font-size:12px;padding:7px}
.pb-btn.is-ghost:hover{color:var(--tx);border-color:var(--fnt);background:var(--card2)}
.pb-btn.is-ship{background:#059669;color:#fff}
.pb-btn.is-advance{background:#2563eb;color:#fff}
.pb-btn.is-ready{background:#059669;color:#fff}
.pb-btn.is-ready:hover{background:#047857}
.pb-btn.is-advance:hover{background:#1d4ed8}
.pb-btn.is-ship:hover{background:#047857}
.pb-btn.is-wait{background:#eef2f8;color:var(--fnt);cursor:not-allowed}

/* Kunlik reja progress */
.pb-target{padding:7px 14px;border-bottom:1px solid var(--bd);background:var(--card2);flex-shrink:0}
.pb-target-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:5px}
.pb-target-l{font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--mut)}
.pb-target-l i{margin-right:4px}
.pb-target-v{font-size:12px;font-weight:800;font-variant-numeric:tabular-nums}
.pb-target-track{height:6px;border-radius:4px;background:#e3e8f0;overflow:hidden}
.pb-target-fill{height:100%;border-radius:4px;transition:width .5s ease}

/* Kartadagi amallar qatori */
.pb-actions{display:flex;gap:6px;margin-top:9px}
.pb-actions .pb-btn{margin-top:0;flex:1}
.pb-actions .pb-hist{flex:0 0 40px;padding:7px;font-size:15px}

/* Offline banner */
.pb-offline{display:flex;align-items:center;gap:10px;padding:9px 16px;background:var(--warn-bg,#fef2f2);color:#991b1b;font-size:13px;font-weight:700;border-bottom:1px solid #fca5a5;flex-shrink:0;animation:pbOfflineIn .2s ease}
.pb-offline i{font-size:18px;color:#dc2626}
.pb-offline b{margin-left:auto;font-family:var(--mono);color:#991b1b;font-weight:700}
@keyframes pbOfflineIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}

/* Shipped */
.pb-shipped{flex-shrink:0;max-height:210px;display:flex;flex-direction:column;background:var(--panel);border-top:2px solid #059669}
.pb-shipped-head{display:flex;align-items:center;gap:8px;padding:8px 16px;font-size:13px;font-weight:800;color:#059669;flex-shrink:0}
.pb-shipped-head .pb-count{background:#059669;margin-left:2px}
.pb-shipped-empty{text-align:center;color:var(--fnt);padding:18px;font-size:13px}
.pb-shipped-scroll{overflow-y:auto}
.pb-tbl{width:100%;border-collapse:collapse;font-size:13px}
.pb-tbl th{position:sticky;top:0;background:var(--card2);text-align:left;font-size:10.5px;letter-spacing:.05em;text-transform:uppercase;color:var(--mut);font-weight:700;padding:8px 14px;border-bottom:1px solid var(--bd)}
.pb-tbl td{padding:8px 14px;border-bottom:1px solid var(--bd);color:var(--tx);font-variant-numeric:tabular-nums}
.pb-tbl tbody tr:hover{background:var(--card2)}
.pb-tbl-no{font-family:var(--mono);font-weight:800;color:#059669}
.pb-tbl-done{color:#059669;font-weight:600;font-family:var(--mono)}
.pb-tbl-mut{color:var(--fnt)}
.pb-tbl-ell{max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

@keyframes pbPulse{0%{box-shadow:0 0 0 0 rgba(5,150,105,.5)}70%{box-shadow:0 0 0 7px rgba(5,150,105,0)}100%{box-shadow:0 0 0 0 rgba(5,150,105,0)}}
@keyframes alarmBlink{0%,100%{opacity:1;box-shadow:0 0 10px rgba(225,29,72,.7),0 0 20px rgba(225,29,72,.4);transform:scale(1.05)}50%{opacity:.5;box-shadow:none;transform:scale(1)}}
.badge-alarm-blink{animation:alarmBlink .75s ease-in-out infinite}
@keyframes alarmBlinkGreen{0%,100%{opacity:1;box-shadow:0 0 10px rgba(5,150,105,.7);transform:scale(1.05)}50%{opacity:.5;box-shadow:none;transform:scale(1)}}
.badge-alarm-blink-green{animation:alarmBlinkGreen .75s ease-in-out infinite}
@keyframes alarmBlinkYellow{0%,100%{opacity:1;box-shadow:0 0 10px rgba(217,119,6,.7);transform:scale(1.05)}50%{opacity:.5;box-shadow:none;transform:scale(1)}}
.badge-alarm-blink-yellow{animation:alarmBlinkYellow .75s ease-in-out infinite}
@media(prefers-reduced-motion:reduce){.badge-alarm-blink,.badge-alarm-blink-green,.badge-alarm-blink-yellow,.pb-live-dot{animation:none}.pb-card{transition:none}}

/* ═══ CHEK JARAYONI NAZORATI (2026-08-01) ═══════════════════════════════════
   DIQQAT: kengashning uslubi FAQAT shu PB_CSS da. Alohida .css fayl
   yaratmang - u import qilinmaydi va jimgina ishlamay qoladi. */

/* Tashlandiq (7+ kun turgan) zakazlarni ochish/yopish */
.pb-abandoned-toggle{display:flex;align-items:center;justify-content:center;gap:6px;width:100%;
	padding:5px 8px;margin-bottom:6px;border:1px dashed var(--bd2);border-radius:7px;
	background:transparent;color:var(--mut);font-size:11.5px;font-weight:700;cursor:pointer}
.pb-abandoned-toggle:hover{background:var(--card2);color:var(--tx)}

/* Kechikkan kartochkada sabab - rahbar KO'RADI, usta/sotuvchi YOZADI */
.pb-late-ask{display:flex;align-items:center;justify-content:center;gap:5px;width:100%;margin-top:6px;
	padding:4px 8px;border:1px dashed #fda29b;border-radius:6px;background:#fff;color:#b42318;
	font-size:11.5px;font-weight:600;cursor:pointer}
.pb-late-ask:hover{background:#fef3f2}
.pb-late-reason{display:flex;align-items:center;justify-content:center;gap:5px;margin-top:6px;
	padding:3px 8px;font-size:11.5px;font-weight:600;color:#92700e;background:#fffaeb;
	border-radius:6px;cursor:pointer}
.pb-late-reason.is-ro{cursor:default}
.pb-late-reason.is-missing{border:1px dashed #fda29b;background:transparent;color:#b42318}

/* Rahbar ro'yxatida to'xtatilgan buyurtma */
.pb-paused{margin:6px 0;padding:8px 10px;border:1px solid #fde68a;border-left:3px solid #d97706;
	border-radius:8px;background:#fffaeb}
.pb-paused-top{display:flex;align-items:center;gap:5px;font-size:11px;font-weight:800;
	letter-spacing:.8px;color:#b45309}
.pb-paused-who{font-size:13px;font-weight:700;color:var(--tx);margin-top:2px}
.pb-paused-why{font-size:12px;color:var(--mut);margin-top:1px}

/* ── Sexdagi TO'XTATILGAN kartochka: yig'ilgan holat ─────────────────────── */
.kb-card.is-paused{background:#fffaf2;border-color:#fde68a;border-left:3px solid #d97706;
	padding:8px 10px;cursor:pointer}
.kb-pz-top{display:flex;align-items:center;gap:6px;min-width:0}
.kb-pz-top>i{color:#d97706;font-size:16px;flex:0 0 auto;font-style:normal}
.kb-pz-no{font-weight:800;font-size:13px;color:var(--tx);flex:0 0 auto;font-family:var(--mono)}
.kb-pz-client{font-size:12.5px;color:var(--mut);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.kb-pz-note{margin-top:3px;padding-left:22px;font-size:11.5px;font-weight:600;color:#b45309;letter-spacing:.2px}
.kb-pz-note b{font-weight:700;color:#92700e}
.kb-pz-why{margin-top:2px;padding-left:22px;font-size:11.5px;color:#78716c}
.kb-pz-wait{display:flex;align-items:center;justify-content:center;gap:5px;margin-top:7px;
	padding:5px 8px;border:1px dashed #d6bcfa;border-radius:7px;background:#faf5ff;
	color:#7c3aed;font-size:11.5px;font-weight:700}
.kb-pz-wait i{font-style:normal}
.kb-pz-go{display:flex;align-items:center;justify-content:center;gap:5px;width:100%;margin-top:7px;
	padding:5px 8px;border:1px solid #059669;border-radius:7px;background:#059669;color:#fff;
	font-size:12px;font-weight:700;cursor:pointer}
.kb-pz-go:hover{background:#047857;border-color:#047857}
.kb-pz-go i{font-style:normal}

/* ── Kartochka ostidagi ixcham amal qatori ───────────────────────────────── */
.kb-tools{display:flex;align-items:center;gap:4px;margin-top:6px;padding-top:6px;border-top:1px dashed var(--bd)}
.kb-tools-sep{flex:1 1 auto}
.kb-tool{display:inline-flex;align-items:center;gap:4px;height:24px;padding:0 8px;border:1px solid transparent;
	border-radius:6px;background:transparent;color:var(--fnt);font-size:11.5px;font-weight:600;
	cursor:pointer;line-height:1}
.kb-tool i{font-size:14px;font-style:normal}
.kb-tool.is-icon{padding:0 6px}
.kb-tool:hover{background:var(--card2);color:var(--mut)}
.kb-tool.need-reason{color:#b42318;border-color:#fecdca;background:#fef3f2}
.kb-tool.has-reason{color:#92700e;border-color:#fde68a;background:#fffaeb;max-width:150px;
	overflow:hidden;white-space:nowrap;text-overflow:ellipsis}

/* ── CHEK YO'LI: jamlanma tepada, vaqt qadamlar ORASIDA ──────────────────── */
.pb-tl-body{max-height:72vh;overflow-y:auto}
.pb-tl-cards{display:flex;gap:8px;margin-bottom:16px}
.pb-tl-card{flex:1 1 0;padding:9px 11px;border:1px solid #e3e8f0;border-radius:9px;background:#f8fafc;min-width:0}
.pb-tl-card-l{display:block;font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:.3px;margin-bottom:3px}
.pb-tl-card b{font-size:17px;font-weight:800;color:#1e293b;font-variant-numeric:tabular-nums}
.pb-tl-card.is-bad{border-color:#fecdca;background:#fef3f2}
.pb-tl-card.is-bad b{color:#b42318}
.pb-tl2{padding-left:4px}
.pb-tl2-step{display:flex;gap:12px;align-items:flex-start}
.pb-tl2-dot{flex:0 0 auto;width:11px;height:11px;margin-top:4px;border-radius:50%;background:#fff;border:2.5px solid #cbd5e1}
.pb-tl2-step.is-start .pb-tl2-dot{border-color:#64748b}
.pb-tl2-step.is-pause .pb-tl2-dot{border-color:#d97706;background:#d97706}
.pb-tl2-step.is-resume .pb-tl2-dot{border-color:#059669;background:#059669}
.pb-tl2-step.is-done .pb-tl2-dot{border-color:#059669;background:#059669}
.pb-tl2-step.is-open .pb-tl2-dot{border-color:#2563eb;border-style:dashed}
.pb-tl2-body{flex:1 1 auto;min-width:0;padding-bottom:2px}
.pb-tl2-head{display:flex;align-items:baseline;justify-content:space-between;gap:10px}
.pb-tl2-title{font-size:13.5px;font-weight:650;color:#1e293b}
.pb-tl2-at{flex:0 0 auto;font-size:12px;color:#94a3b8;font-variant-numeric:tabular-nums}
.pb-tl2-detail{font-size:12.5px;color:#64748b;margin-top:2px}
.pb-tl2-detail .uil-arrow-right{color:#2563eb;margin:0 4px;font-style:normal}
.pb-tl2-old{color:#94a3b8;text-decoration:line-through}
.pb-tl2-dept{margin-left:6px;padding:1px 6px;border-radius:5px;background:#eef2f8;color:#64748b;font-size:10.5px;font-weight:700;letter-spacing:.3px;vertical-align:middle}
.pb-tl2-user{font-size:11.5px;color:#94a3b8;margin-top:1px}
.pb-tl2-step.is-open .pb-tl2-title{color:#1d4ed8}
.pb-tl2-step.is-open .pb-tl2-at{color:#1d4ed8;font-weight:700}
.pb-tl2-gap{margin:2px 0 2px 5px;padding:3px 0 3px 19px;border-left:2px dotted #e3e8f0}
.pb-tl2-gap span{font-size:11.5px;color:#94a3b8;font-variant-numeric:tabular-nums}
`

export default Board
